import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';

const MixCutPage = () => {
  const [activeTab, setActiveTab] = useState('oral');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [tasks, setTasks] = useState([]);
  
  // 分页状态
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;
  const [resultsPage, setResultsPage] = useState(1);
  const resultsPageSize = 10;
  
  // 已完成的任务列表（用于剪辑结果页面）：展开 result_urls 为独立视频卡片
  const completedTasks = tasks
    .filter(task => task.status === 'completed')
    .flatMap(task => {
      const urls = task.result_urls && task.result_urls.length > 0 ? task.result_urls : (task.result_url ? [task.result_url] : []);
      return urls.map((url, index) => ({
        ...task,
        display_url: url,
        display_index: urls.length > 1 ? index + 1 : 0,
        display_total: urls.length
      }));
    });

  // 已完成任务按任务分组（用于任务列表的视频合集展示）
  const completedTasksByTask = tasks
    .filter(task => task.status === 'completed')
    .map(task => {
      const urls = task.result_urls && task.result_urls.length > 0 ? task.result_urls : (task.result_url ? [task.result_url] : []);
      return { ...task, video_urls: urls, video_count: urls.length };
    })
    .filter(task => task.video_count > 0);  // 只展示有视频的任务

  // 分页结果
  const totalResultsGroups = completedTasksByTask.length;
  const totalResultsPages = Math.max(1, Math.ceil(totalResultsGroups / resultsPageSize));
  const paginatedResults = completedTasksByTask.slice(
    (resultsPage - 1) * resultsPageSize,
    resultsPage * resultsPageSize
  );
  
  const totalCompletedVideos = completedTasks.length;

  // 批量子选择状态：Set of "taskIdx:vIdx"
  const [selectedVideos, setSelectedVideos] = useState(new Set());
  const [batchDownloading, setBatchDownloading] = useState(false);

  const toggleVideoSelect = (key) => {
    setSelectedVideos(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const selectAllVideos = () => {
    const all = new Set();
    completedTasksByTask.forEach((task, tIdx) => {
      task.video_urls.forEach((_, vIdx) => all.add(`${tIdx}:${vIdx}`));
    });
    setSelectedVideos(all);
  };

  const deselectAllVideos = () => {
    setSelectedVideos(new Set());
  };

  const batchDownloadVideos = async () => {
    if (selectedVideos.size === 0) {
      setMessage({ type: 'error', text: '请先勾选要下载的视频' });
      return;
    }
    setBatchDownloading(true);
    let count = 0;
    for (const key of selectedVideos) {
      const [tIdx, vIdx] = key.split(':').map(Number);
      const task = completedTasksByTask[tIdx];
      if (!task) continue;
      const url = task.video_urls[vIdx];
      if (!url) continue;
      const filename = `${task.task_name || 'video'}${task.video_count > 1 ? '_' + (vIdx + 1) : ''}.mp4`;
      await downloadVideo(url, filename);
      count++;
      // 小延迟避免浏览器阻止连续下载
      await new Promise(r => setTimeout(r, 300));
    }
    setBatchDownloading(false);
    setMessage({ type: 'success', text: `已触发 ${count} 个视频下载` });
  };

  // 过期任务
  const expiredTasks = tasks.filter(t => t.status === 'expired');
  
  const cleanExpiredTasks = async () => {
    const token = localStorage.getItem('token');
    let cleaned = 0;
    for (const t of expiredTasks) {
      try {
        await fetch(`/api/v1/mixcut/delete/${t.task_id}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        cleaned++;
      } catch (e) {}
    }
    fetchTasks();
    setMessage({ type: 'success', text: `已清理 ${cleaned} 条过期记录` });
  };

  // 已完成但无视频的 "孤儿" 任务数
  const orphanCompletedCount = tasks.filter(t => 
    t.status === 'completed' && 
    !t.result_url && 
    (!t.result_urls || t.result_urls.length === 0)
  ).length;
  
  const [mediaList, setMediaList] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef(null);
  const bgmFileInputRef = useRef(null);
  const shotFileInputRef = useRef(null);
  
  // 上传分组
  const [uploadGroup, setUploadGroup] = useState('');
  const [groupList, setGroupList] = useState([]);
  const [mediaGroupFilter, setMediaGroupFilter] = useState('');
  const [uploadMediaType, setUploadMediaType] = useState('video'); // 'video'|'image'|'audio'|'all'
  
  const [newShotScript, setNewShotScript] = useState({
    scriptText: '',
    duration: '5',
    selectedMedia: []       // 多素材选择改为数组
  });

  // 为镜头添加/移除素材
  const toggleNewShotMedia = (url) => {
    setNewShotScript(prev => ({
      ...prev,
      selectedMedia: prev.selectedMedia.includes(url)
        ? prev.selectedMedia.filter(u => u !== url)
        : [...prev.selectedMedia, url]
    }));
  };

  const removeNewShotMedia = (url) => {
    setNewShotScript(prev => ({
      ...prev,
      selectedMedia: prev.selectedMedia.filter(u => u !== url)
    }));
  };

  // 为已有镜头添加/移除素材
  const toggleShotMedia = (shotIndex, url) => {
    setMultiShotConfig(prev => {
      const newShots = [...prev.shot_scripts];
      const shot = { ...newShots[shotIndex] };
      const arr = shot.MediaArray || [];
      shot.MediaArray = arr.includes(url) ? arr.filter(u => u !== url) : [...arr, url];
      newShots[shotIndex] = shot;
      return { ...prev, shot_scripts: newShots };
    });
  };

  const removeShotMedia = (shotIndex, url) => {
    setMultiShotConfig(prev => {
      const newShots = [...prev.shot_scripts];
      const shot = { ...newShots[shotIndex] };
      shot.MediaArray = (shot.MediaArray || []).filter(u => u !== url);
      newShots[shotIndex] = shot;
      return { ...prev, shot_scripts: newShots };
    });
  };
  
  // 视频预览弹窗状态
  const [previewVideo, setPreviewVideo] = useState(null);
  
  // 生成OSS视频快照URL（单个）
  const getVideoSnapshot = (videoUrl, timeMs = 1000) => {
    if (!videoUrl) return null;
    const baseUrl = videoUrl.split('?')[0];
    // 使用4:3比例 480x360
    return `${baseUrl}?x-oss-process=video/snapshot,t_${timeMs},f_jpg,w_480,h_360`;
  };
  
  // 生成3x3网格快照时间点（从1秒到9秒）
  const getSnapshotTimes = () => [1000, 2000, 3000, 4000, 5000, 6000, 7000, 8000, 9000];
  
  // 格式化任务名称显示
  const getTaskTypeName = (taskType) => {
    const typeMap = {
      'oral_broadcast': '口播模式',
      'multi_shot': '多镜头脚本',
      'quick': '快速成片'
    };
    return typeMap[taskType] || taskType || '未知';
  };
  
  // 格式化时间
  const formatTime = (timeStr) => {
    if (!timeStr) return '-';
    try {
      const d = new Date(timeStr);
      return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')} ${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
    } catch {
      return timeStr;
    }
  };
  
  const formatDuration = (seconds) => {
    if (seconds == null) return '-';
    const s = parseInt(seconds);
    if (s < 60) return `${s}秒`;
    if (s < 3600) return `${Math.floor(s/60)}分${s%60}秒`;
    return `${Math.floor(s/3600)}时${Math.floor((s%3600)/60)}分${s%60}秒`;
  };
  
  // 下载视频
  const downloadVideo = async (url, filename) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename || 'video.mp4';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      setMessage({ type: 'error', text: '下载失败: ' + err.message });
    }
  };
  
  // 口播模式混剪状态
  const [oralConfig, setOralConfig] = useState({
    task_name: '',
    media_urls: [],
    speech_text: '',
    voice: 'zhiru',
    bgm_urls: [],
    bgm_volume: 0.8,
    output_count: 1,
    max_duration: 60,
    video_size: '9:16'
  });
  
  const getVideoDimensions = (size) => {
    return size === '16:9' ? { width: 1920, height: 1080 } : { width: 1080, height: 1920 };
  };

  // 多镜头脚本混剪状态
  const [multiShotConfig, setMultiShotConfig] = useState({
    task_name: '',
    shot_scripts: [],
    bgm_urls: [],
    bgm_volume: 0.8,
    material_volume: 0,
    title: '',
    title_color: '#000000',
    title_bgcolor: '#ffffff',
    title_position: '距离顶部20%',
    title_font_size: 60,
    output_count: 3,
    video_size: '9:16'
  });
  const [multiShotStep, setMultiShotStep] = useState('basic');

  // 快速成片状态
  const [quickConfig, setQuickConfig] = useState({
    task_name: '',
    media_urls: [],
    bgm_urls: [],
    bgm_volume: 0.8,
    template_id: '',
    output_count: 1,
    max_duration: 30
  });

  useEffect(() => {
    fetchTasks();
    fetchMediaList();
    fetchGroups();
  }, []);

  const fetchTasks = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/v1/mixcut/list', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setTasks(data.tasks || []);
      }
    } catch (err) {
      console.error('获取任务列表失败:', err);
    }
  };

  // 获取分组筛选后的素材列表
  const getFilteredMedia = (typeFilter) => {
    let list = mediaList;
    if (typeFilter) {
      list = list.filter(m => typeFilter(m.type));
    }
    if (mediaGroupFilter) {
      list = list.filter(m => (m.group || '') === mediaGroupFilter || (mediaGroupFilter === '__ungrouped__' && !m.group));
    }
    return list;
  };
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editTaskId, setEditTaskId] = useState(null);
  const [editTaskName, setEditTaskName] = useState('');
  const [editOutputCount, setEditOutputCount] = useState(1);
  const [editMaxDuration, setEditMaxDuration] = useState(30);

  const handleEditTask = (task) => {
    setEditTaskId(task.task_id);
    setEditTaskName(task.task_name || '');
    setEditOutputCount(task.output_count || task.config?.output_count || 1);
    setEditMaxDuration(task.max_duration || task.config?.max_duration || 30);
    setEditModalOpen(true);
  };

  const saveEditTask = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/v1/mixcut/edit/${editTaskId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ task_name: editTaskName, output_count: editOutputCount, max_duration: editMaxDuration })
      });
      const data = await res.json();
      if (data.success) {
        setMessage({ type: 'success', text: '任务编辑成功' });
        setEditModalOpen(false);
        fetchTasks();
      } else {
        setMessage({ type: 'error', text: data.message || '编辑失败' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: '编辑失败: ' + err.message });
    }
  };

  const handleCopyTask = async (task_id) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/v1/mixcut/copy/${task_id}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setMessage({ type: 'success', text: '任务复制成功！' });
        fetchTasks();
      } else {
        setMessage({ type: 'error', text: data.message || '复制失败' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: '复制失败: ' + err.message });
    }
    setLoading(false);
  };

  const deleteTask = async (task_id) => {
    if (!window.confirm('确定要删除这个任务吗？')) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/v1/mixcut/delete/${task_id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setMessage({ type: 'success', text: '任务删除成功' });
        setTasks(prev => prev.filter(t => t.task_id !== task_id));
      } else {
        setMessage({ type: 'error', text: data.message || '删除失败' });
        fetchTasks();
      }
    } catch (err) {
      setMessage({ type: 'error', text: '删除失败: ' + err.message });
      fetchTasks();
    }
  };

  // 分页计算
  const totalTasks = tasks.length;
  const totalPages = Math.ceil(totalTasks / pageSize);
  let paginatedTasks = tasks;
  if (totalTasks > pageSize) {
    paginatedTasks = tasks.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  }
  
  // 分页渲染函数
  const RenderPagination = () => {
    if (totalPages <= 1) return null;
    return (
      <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '1rem', marginTop: '1.5rem', padding: '1rem', background: '#f8f9fa', borderRadius: '8px'}}>
        <button disabled={currentPage === 1} onClick={() => setCurrentPage(p => Math.max(1, p - 1))} style={{padding: '0.5rem 1rem', border: '1px solid #dee2e6', borderRadius: '5px', background: 'white', cursor: 'pointer', opacity: currentPage === 1 ? 0.5 : 1}}>上一页</button>
        <span style={{fontSize: '0.85rem', color: '#666'}}>第 {currentPage} / {totalPages} 页，共 {totalTasks} 条</span>
        <button disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} style={{padding: '0.5rem 1rem', border: '1px solid #dee2e6', borderRadius: '5px', background: 'white', cursor: 'pointer', opacity: currentPage === totalPages ? 0.5 : 1}}>下一页</button>
      </div>
    );
  };

  const fetchMediaList = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/v1/mixcut/media/list', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setMediaList(data.data.media_list || []);
      }
    } catch (err) {
      console.error('获取素材列表失败:', err);
    }
  };

  const fetchGroups = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/v1/mixcut/media/groups', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setGroupList(data.data.groups || []);
      }
    } catch (err) {
      console.error('获取分组列表失败:', err);
    }
  };

  const handleFileUpload = async (event, type = 'media', groupName = '') => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    setUploadProgress(0);
    setMessage(null);

    const effectiveGroup = groupName || uploadGroup;

    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        if (effectiveGroup) {
          formData.append('group', effectiveGroup);
        }

        const token = localStorage.getItem('token');
        const res = await fetch('/api/v1/mixcut/upload', {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData
        });

        const data = await res.json();
        if (data.success) {
          setMessage({ type: 'success', text: `${file.name} 上传成功！` });
          fetchMediaList();
          fetchGroups();
          
          if (type === 'media') {
            setOralConfig(prev => ({
              ...prev,
              media_urls: [...prev.media_urls, data.data.url]
            }));
          } else if (type === 'bgm') {
            setOralConfig(prev => ({
              ...prev,
              bgm_urls: [...prev.bgm_urls, data.data.url]
            }));
          } else if (type === 'multi_bgm') {
            setMultiShotConfig(prev => ({
              ...prev,
              bgm_urls: [...prev.bgm_urls, data.data.url]
            }));
          } else if (type === 'quick') {
            setQuickConfig(prev => ({
              ...prev,
              media_urls: [...prev.media_urls, data.data.url]
            }));
          } else if (type === 'quick_bgm') {
            setQuickConfig(prev => ({
              ...prev,
              bgm_urls: [...prev.bgm_urls, data.data.url]
            }));
          } else if (type === 'shot_media') {
            setNewShotScript(prev => ({
              ...prev,
              selectedMedia: [...prev.selectedMedia, data.data.url]
            }));
          }
        } else {
          setMessage({ type: 'error', text: data.message || '上传失败' });
        }
      } catch (err) {
        setMessage({ type: 'error', text: `上传失败: ${err.message}` });
      }
    }

    setUploading(false);
    setUploadProgress(100);
    event.target.value = '';
  };

  const selectFromMediaList = (url, type = 'media') => {
    if (type === 'media') {
      setOralConfig(prev => ({
        ...prev,
        media_urls: [...prev.media_urls, url]
      }));
    } else if (type === 'bgm') {
      setOralConfig(prev => ({
        ...prev,
        bgm_urls: [...prev.bgm_urls, url]
      }));
    }
    setMessage({ type: 'success', text: '已添加到素材列表' });
  };

  const deleteMedia = async (index) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/v1/mixcut/media/${index}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setMessage({ type: 'success', text: '删除成功' });
        fetchMediaList();
        fetchGroups();
      }
    } catch (err) {
      setMessage({ type: 'error', text: `删除失败: ${err.message}` });
    }
  };

  const updateMediaGroup = async (index, groupName) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/v1/mixcut/media/${index}/group`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ group: groupName })
      });
      const data = await res.json();
      if (data.success) {
        fetchMediaList();
        fetchGroups();
      } else {
        setMessage({ type: 'error', text: data.message || '分组更新失败' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: `分组更新失败: ${err.message}` });
    }
  };

  // 分组编辑状态：{ index: true/false } 控制内联编辑
  const [editingGroup, setEditingGroup] = useState({});
  const [editGroupValue, setEditGroupValue] = useState('');

  const startEditGroup = (index, currentGroup) => {
    setEditingGroup(prev => ({ ...prev, [index]: true }));
    setEditGroupValue(currentGroup || '');
  };
  const saveGroup = (index) => {
    updateMediaGroup(index, editGroupValue);
    setEditingGroup(prev => ({ ...prev, [index]: false }));
  };

  const checkTaskStatus = async (taskId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/v1/mixcut/ice/status/${taskId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        fetchTasks();
        if (data.data.status === 'processing' || data.data.status === 'submitted') {
          setTimeout(() => checkTaskStatus(taskId), 5000);
        }
      }
    } catch (err) {
      console.error('查询任务状态失败:', err);
    }
  };

  const refreshAllTasks = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/v1/mixcut/ice/refresh-all', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setMessage({ type: 'success', text: `已刷新 ${data.data?.updated_count || 0} 个任务状态` });
        fetchTasks();
      } else {
        setMessage({ type: 'error', text: data.message || '刷新失败' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: '刷新失败: ' + err.message });
    }
    setLoading(false);
  };

  const handleSubmitOralMixCut = async () => {
    if (!oralConfig.task_name.trim()) {
      setMessage({ type: 'error', text: '请填写任务名称' });
      return;
    }
    if (!oralConfig.media_urls.length) {
      setMessage({ type: 'error', text: '请上传素材' });
      return;
    }
    if (!oralConfig.speech_text.trim()) {
      setMessage({ type: 'error', text: '请填写口播文案' });
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      const token = localStorage.getItem('token');
      const dims = getVideoDimensions(oralConfig.video_size);
      const res = await fetch('/api/v1/mixcut/ice/oral', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...oralConfig,
          width: dims.width,
          height: dims.height
        })
      });

      const data = await res.json();
      if (data.success) {
        setMessage({ type: 'success', text: '口播混剪任务提交成功！' });
        fetchTasks();
        if (data.data.task_id) {
          setTimeout(() => checkTaskStatus(data.data.task_id), 3000);
        }
      } else {
        setMessage({ type: 'error', text: data.message || '提交失败' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: '提交失败: ' + err.message });
    }
    setLoading(false);
  };

  const handleSubmitMultiShot = async () => {
    if (!multiShotConfig.task_name.trim()) {
      setMessage({ type: 'error', text: '请填写任务名称' });
      return;
    }
    if (!multiShotConfig.shot_scripts.length) {
      setMessage({ type: 'error', text: '请添加分镜脚本' });
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      const token = localStorage.getItem('token');
      const dims = getVideoDimensions(multiShotConfig.video_size);
      const res = await fetch('/api/v1/mixcut/ice/multi-shot', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...multiShotConfig,
          width: dims.width,
          height: dims.height
        })
      });

      const data = await res.json();
      if (data.success) {
        setMessage({ type: 'success', text: '多镜头混剪任务提交成功！' });
        fetchTasks();
        if (data.data.task_id) {
          setTimeout(() => checkTaskStatus(data.data.task_id), 3000);
        }
      } else {
        setMessage({ type: 'error', text: data.message || '提交失败' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: '提交失败: ' + err.message });
    }
    setLoading(false);
  };

  const handleSubmitQuick = async () => {
    if (!quickConfig.task_name.trim()) {
      setMessage({ type: 'error', text: '请填写任务名称' });
      return;
    }
    if (!quickConfig.media_urls.length) {
      setMessage({ type: 'error', text: '请上传素材' });
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/v1/mixcut/ice/quick', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(quickConfig)
      });

      const data = await res.json();
      if (data.success) {
        setMessage({ type: 'success', text: '快速成片任务提交成功！' });
        fetchTasks();
        if (data.data.task_id) {
          setTimeout(() => checkTaskStatus(data.data.task_id), 3000);
        }
      } else {
        setMessage({ type: 'error', text: data.message || '提交失败' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: '提交失败: ' + err.message });
    }
    setLoading(false);
  };

  const removeMediaUrl = (config, setConfig, index, field = 'media_urls') => {
    setConfig({
      ...config,
      [field]: config[field].filter((_, i) => i !== index)
    });
  };

  const addShotScript = () => {
    const duration = parseFloat(newShotScript.duration);
    if (!duration || duration <= 0) {
      setMessage({ type: 'error', text: '请输入有效的镜头时间（秒）' });
      return;
    }
    
    setMultiShotConfig(prev => ({
      ...prev,
      shot_scripts: [
        ...prev.shot_scripts,
        {
          ScriptText: newShotScript.scriptText.trim(),
          SubtitleText: newShotScript.scriptText.trim(),
          Duration: duration,
          MediaArray: [...newShotScript.selectedMedia]
        }
      ]
    }));
    setNewShotScript({
      scriptText: '',
      duration: '5',
      selectedMedia: []
    });
    setMessage({ type: 'success', text: '镜头已添加' });
  };

  const removeShotScript = (index) => {
    setMultiShotConfig({
      ...multiShotConfig,
      shot_scripts: multiShotConfig.shot_scripts.filter((_, i) => i !== index)
    });
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  const getMediaIcon = (type) => {
    switch(type) {
      case 'video': return '🎬';
      case 'audio': return '🎵';
      case 'image': return '🖼️';
      default: return '📄';
    }
  };

  // 判断是否为视频URL（用于缩略图显示）
  const isVideoUrl = (url) => {
    return url && (url.match(/\.(mp4|mov|avi|mkv|webm)(\?|$)/i) || url.includes('video'));
  };

  // 生成OSS视频快照URL
  const getMediaThumbnail = (media) => {
    if (!media || !media.url) return null;
    // 视频文件使用OSS快照
    if (media.type === 'video' || isVideoUrl(media.url)) {
      const baseUrl = media.url.split('?')[0];
      return `${baseUrl}?x-oss-process=video/snapshot,t_1000,f_jpg,w_480,h_360`;
    }
    // 图片文件直接返回URL
    if (media.type === 'image') {
      return media.url;
    }
    return null;
  };

  return (
    <PageContainer>
      <Header>
        <Logo>
          <LogoIcon>🎬</LogoIcon>
          <LogoText>视频混剪</LogoText>
        </Logo>
        <UserMenu>
          <ActionButton onClick={fetchTasks}>刷新任务</ActionButton>
        </UserMenu>
      </Header>

      <MainContent>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          {/* 混剪模式选择 */}
          <TabContainer>
            {[
              { key: 'oral', label: '🎤 口播模式混剪', gradient: 'linear-gradient(135deg, #667eea, #764ba2)' },
              { key: 'multi-shot', label: '🎥 多镜头脚本混剪', gradient: 'linear-gradient(135deg, #fa709a, #fee140)' },
              { key: 'quick', label: '⚡ 快速成片', gradient: 'linear-gradient(135deg, #43e97b, #38f9d7)' },
              { key: 'results', label: '📹 剪辑结果', gradient: 'linear-gradient(135deg, #ff9a9e, #fecfef)' }
            ].map(tab => (
              <TabButton
                key={tab.key}
                gradient={tab.gradient}
                onClick={() => { setActiveTab(tab.key); setMessage(null); }}
                $active={activeTab === tab.key}
              >
                {tab.label}
              </TabButton>
            ))}
          </TabContainer>

          {/* 消息提示 */}
          {message && (
            <MessageBox type={message.type}>
              {message.text}
            </MessageBox>
          )}

          {/* 口播模式混剪 */}
          {activeTab === 'oral' && (
            <ConfigCard>
              <ConfigTitle>🎤 口播模式混剪</ConfigTitle>
              <InfoBox>
                <h4>📌 功能说明</h4>
                <p>
                  将多个视频/图片素材与口播文案智能混剪，一键批量合成视频<br/>
                  支持自动语音合成、字幕生成、背景音乐添加
                </p>
              </InfoBox>

              <FormGroup>
                <Label>任务名称</Label>
                <Input
                  type="text"
                  placeholder="请输入任务名称"
                  value={oralConfig.task_name}
                  onChange={(e) => setOralConfig({...oralConfig, task_name: e.target.value})}
                />
              </FormGroup>

              <FormGroup>
                <Label>视频/图片素材</Label>
                <MediaGroupSelect>
                  <SmallLabel>上传分组：</SmallLabel>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                    <GroupInput 
                      placeholder="输入分组名称（可选）" 
                      value={uploadGroup}
                      onChange={(e) => setUploadGroup(e.target.value)}
                    />
                    {groupList.map(g => (
                      <GroupTag key={g.name} $active={uploadGroup === g.name} onClick={() => setUploadGroup(uploadGroup === g.name ? '' : g.name)}>
                        {g.name} ({g.count})
                      </GroupTag>
                    ))}
                  </div>
                </MediaGroupSelect>
                <UploadTypeTabs>
                  {[
                    { key: 'video', label: '📹 视频', accept: 'video/*' },
                    { key: 'image', label: '🖼️ 图片', accept: 'image/*' },
                    { key: 'audio', label: '🎵 音频', accept: 'audio/*' },
                    { key: 'all', label: '📂 全部', accept: 'video/*,image/*,audio/*' }
                  ].map(t => (
                    <UploadTypeTab key={t.key} $active={uploadMediaType === t.key} onClick={() => setUploadMediaType(t.key)}>
                      {t.label}
                    </UploadTypeTab>
                  ))}
                </UploadTypeTabs>
                <UploadArea>
                  <UploadButton onClick={() => fileInputRef.current?.click()} disabled={uploading}>
                    {uploading ? '上传中...' : `📤 上传${uploadMediaType === 'audio' ? '音频' : uploadMediaType === 'image' ? '图片' : '素材'}`}
                  </UploadButton>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept={uploadMediaType === 'all' ? 'video/*,image/*,audio/*' : uploadMediaType === 'audio' ? 'audio/*' : uploadMediaType === 'image' ? 'image/*' : 'video/*'}
                    multiple
                    style={{ display: 'none' }}
                    onChange={(e) => handleFileUpload(e, uploadMediaType === 'audio' ? 'bgm' : 'media')}
                  />
                  {uploading && <UploadProgress>{uploadProgress}%</UploadProgress>}
                </UploadArea>
                
                <SelectedMediaList>
                  {oralConfig.media_urls.map((url, index) => (
                    <SelectedMediaItem key={index}>
                      <MediaIcon>📎</MediaIcon>
                      <MediaUrl>{url}</MediaUrl>
                      <RemoveButton onClick={() => removeMediaUrl(oralConfig, setOralConfig, index)}>
                        ✕
                      </RemoveButton>
                    </SelectedMediaItem>
                  ))}
                </SelectedMediaList>

                <MediaLibrarySection>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem', flexWrap: 'wrap', gap: '0.5rem' }}>
                    <MediaLibraryTitle>📚 素材库（{getFilteredMedia(t => t !== 'audio').length}个视频/图片）</MediaLibraryTitle>
                    <GroupFilterRow>
                      <SmallLabel>分组筛选：</SmallLabel>
                      <Select value={mediaGroupFilter} onChange={(e) => setMediaGroupFilter(e.target.value)} style={{ width: '160px' }}>
                        <option value="">全部素材</option>
                        <option value="__ungrouped__">未分组</option>
                        {groupList.map(g => (
                          <option key={g.name} value={g.name}>{g.name} ({g.count})</option>
                        ))}
                      </Select>
                    </GroupFilterRow>
                  </div>
                  {getFilteredMedia(t => t !== 'audio').length === 0 ? (
                    <EmptyMediaLibrary>暂无视频/图片素材，请先上传</EmptyMediaLibrary>
                  ) : (
                    <MediaLibraryGrid>
                      {getFilteredMedia(t => t !== 'audio').map((media, index) => (
                        <MediaLibraryItem key={index}>
                          {media.group && <MediaGroupBadge>{media.group}</MediaGroupBadge>}
                          <MediaThumbnail>
                            {getMediaThumbnail(media) ? (
                              <ThumbnailImg 
                                src={getMediaThumbnail(media)} 
                                alt={media.filename}
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'flex';
                                }}
                              />
                            ) : null}
                            <ThumbnailFallback style={getMediaThumbnail(media) ? { display: 'none' } : {}}>
                              <MediaIconLarge>{getMediaIcon(media.type)}</MediaIconLarge>
                            </ThumbnailFallback>
                          </MediaThumbnail>
                          <MediaInfo>
                            <MediaFileName>{media.filename}</MediaFileName>
                            <MediaSize>{formatFileSize(media.size)}</MediaSize>
                            <GroupEditLine>
                              {editingGroup[index] ? (
                                <GroupEditForm onSubmit={(e) => { e.preventDefault(); saveGroup(index); }}>
                                  <input value={editGroupValue} onChange={(e) => setEditGroupValue(e.target.value)}
                                    style={{ width: '70px', padding: '2px 4px', fontSize: '0.7rem', background: 'var(--bg-card)', border: '1px solid var(--border-color)', color: 'var(--text-primary)', borderRadius: '3px' }}
                                    autoFocus onBlur={() => saveGroup(index)} />
                                </GroupEditForm>
                              ) : (
                                <GroupEditTag onClick={() => startEditGroup(index, media.group)} title="点击修改分组">
                                  {media.group || '未分组'} ✎
                                </GroupEditTag>
                              )}
                            </GroupEditLine>
                          </MediaInfo>
                          <MediaActions>
                            <AddToTaskButton onClick={() => selectFromMediaList(media.url, 'media')}>
                              + 添加
                            </AddToTaskButton>
                            <DeleteMediaButton onClick={() => deleteMedia(index)}>
                              🗑️
                            </DeleteMediaButton>
                          </MediaActions>
                        </MediaLibraryItem>
                      ))}
                    </MediaLibraryGrid>
                  )}
                </MediaLibrarySection>
              </FormGroup>

              <FormGroup>
                <Label>口播文案</Label>
                <TextArea
                  rows="4"
                  placeholder="请输入口播文案内容..."
                  value={oralConfig.speech_text}
                  onChange={(e) => setOralConfig({...oralConfig, speech_text: e.target.value})}
                />
              </FormGroup>

              <FormGroup>
                <Label>口播音色</Label>
                <Select
                  value={oralConfig.voice}
                  onChange={(e) => setOralConfig({...oralConfig, voice: e.target.value})}
                >
                  <option value="zhiru">知如（女声-温柔）</option>
                  <option value="zhilun">知伦（男声-沉稳）</option>
                  <option value="zhibei_emo">知贝（男声-情感）</option>
                  <option value="zhichu">知初（女声-甜美）</option>
                </Select>
              </FormGroup>

              <FormGroup>
                <Label>背景音乐（可选）</Label>
                <MediaGroupSelect>
                  <SmallLabel>上传分组：</SmallLabel>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                    <GroupInput placeholder="输入分组名称（可选）" value={uploadGroup}
                      onChange={(e) => setUploadGroup(e.target.value)} />
                    {groupList.map(g => (
                      <GroupTag key={g.name} $active={uploadGroup === g.name} onClick={() => setUploadGroup(uploadGroup === g.name ? '' : g.name)}>
                        {g.name} ({g.count})
                      </GroupTag>
                    ))}
                  </div>
                </MediaGroupSelect>
                <BgmUploadArea>
                  <BgmUploadButton onClick={() => bgmFileInputRef.current?.click()} disabled={uploading}>
                    {uploading ? '上传中...' : '🎵 上传背景音乐'}
                  </BgmUploadButton>
                  <input
                    ref={bgmFileInputRef}
                    type="file"
                    accept="audio/*"
                    style={{ display: 'none' }}
                    onChange={(e) => handleFileUpload(e, 'bgm')}
                  />
                </BgmUploadArea>
                {oralConfig.bgm_urls && oralConfig.bgm_urls.length > 0 && (
                  <SelectedMediaList>
                    {oralConfig.bgm_urls.map((url, idx) => (
                      <SelectedMediaItem key={idx}>
                        <MediaIcon>🎵</MediaIcon>
                        <BgmUrl>{url}</BgmUrl>
                        <RemoveButton onClick={() => setOralConfig({...oralConfig, bgm_urls: oralConfig.bgm_urls.filter((_, i) => i !== idx)})}>✕</RemoveButton>
                      </SelectedMediaItem>
                    ))}
                  </SelectedMediaList>
                )}
                <BgmLibrarySection>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem', flexWrap: 'wrap', gap: '0.5rem' }}>
                    <BgmLibraryTitle>📚 音频素材库（{getFilteredMedia(t => t === 'audio').length}个）</BgmLibraryTitle>
                    <GroupFilterRow>
                      <SmallLabel>分组筛选：</SmallLabel>
                      <Select value={mediaGroupFilter} onChange={(e) => setMediaGroupFilter(e.target.value)} style={{ width: '160px' }}>
                        <option value="">全部音频</option>
                        <option value="__ungrouped__">未分组</option>
                        {groupList.map(g => (
                          <option key={g.name} value={g.name}>{g.name} ({g.count})</option>
                        ))}
                      </Select>
                    </GroupFilterRow>
                  </div>
                  <MediaLibraryGrid>
                    {getFilteredMedia(t => t === 'audio').map((media, index) => (
                      <MediaLibraryItem key={index}>
                        <MediaThumbnail>
                          <AudioPlayerInline controls>
                            <source src={media.url} type="audio/mpeg" />
                            <source src={media.url} type="audio/wav" />
                            <source src={media.url} type="audio/mp3" />
                            不支持音频播放
                          </AudioPlayerInline>
                        </MediaThumbnail>
                        <MediaInfo>
                          <MediaFileName>{media.filename}</MediaFileName>
                          <MediaSize>{formatFileSize(media.size)}</MediaSize>
                          <GroupEditLine>
                            {editingGroup[index] ? (
                              <GroupEditForm onSubmit={(e) => { e.preventDefault(); saveGroup(index); }}>
                                <input value={editGroupValue} onChange={(e) => setEditGroupValue(e.target.value)}
                                  style={{ width: '70px', padding: '2px 4px', fontSize: '0.7rem', background: 'var(--bg-card)', border: '1px solid var(--border-color)', color: 'var(--text-primary)', borderRadius: '3px' }}
                                  autoFocus onBlur={() => saveGroup(index)} />
                              </GroupEditForm>
                            ) : (
                              <GroupEditTag onClick={() => startEditGroup(index, media.group)} title="点击修改分组">
                                {media.group || '未分组'} ✎
                              </GroupEditTag>
                            )}
                          </GroupEditLine>
                        </MediaInfo>
                        <MediaActions>
                          <AddToTaskButton onClick={() => selectFromMediaList(media.url, 'bgm')}>
                            + 添加
                          </AddToTaskButton>
                          <DeleteMediaButton onClick={() => deleteMedia(index)}>
                            🗑️
                          </DeleteMediaButton>
                        </MediaActions>
                      </MediaLibraryItem>
                    ))}
                  </MediaLibraryGrid>
                </BgmLibrarySection>
              </FormGroup>

              <FormGroup>
                <Label>背景音乐音量: {oralConfig.bgm_volume}</Label>
                <Input
                  type="range"
                  min="0"
                  max="1"
                  step="0.1"
                  value={oralConfig.bgm_volume}
                  onChange={(e) => setOralConfig({...oralConfig, bgm_volume: parseFloat(e.target.value)})}
                />
              </FormGroup>

              <FormRow>
                <FormGroup>
                  <Label>输出视频数量</Label>
                  <Input
                    type="number"
                    min="1"
                    max="50"
                    value={oralConfig.output_count}
                    onChange={(e) => setOralConfig({...oralConfig, output_count: parseInt(e.target.value)})}
                  />
                </FormGroup>

                <FormGroup>
                  <Label>背景音乐（可选）</Label>
                  <BgmUploadArea>
                    <BgmUploadButton onClick={() => bgmFileInputRef.current?.click()} disabled={uploading}>
                      {uploading ? '上传中...' : '🎵 上传背景音乐'}
                    </BgmUploadButton>
                    <input ref={bgmFileInputRef} type="file" accept="audio/*" style={{ display: 'none' }}
                      onChange={(e) => handleFileUpload(e, 'quick_bgm')} />
                  </BgmUploadArea>
                  {quickConfig.bgm_urls && quickConfig.bgm_urls.length > 0 && (
                    <SelectedMediaList>
                      {quickConfig.bgm_urls.map((url, idx) => (
                        <SelectedMediaItem key={idx}>
                          <MediaIcon>🎵</MediaIcon>
                          <BgmUrl>{url}</BgmUrl>
                          <RemoveButton onClick={() => setQuickConfig({...quickConfig, bgm_urls: quickConfig.bgm_urls.filter((_, i) => i !== idx)})}>✕</RemoveButton>
                        </SelectedMediaItem>
                      ))}
                    </SelectedMediaList>
                  )}
                  <Label style={{ marginTop: '0.5rem' }}>BGM音量: {quickConfig.bgm_volume}</Label>
                  <Input type="range" min="0" max="1" step="0.1" value={quickConfig.bgm_volume}
                    onChange={(e) => setQuickConfig({...quickConfig, bgm_volume: parseFloat(e.target.value)})} />
                </FormGroup>

                <FormGroup>
                  <Label>最大时长（秒）</Label>
                  <Input
                    type="number"
                    min="5"
                    max="300"
                    value={oralConfig.max_duration}
                    onChange={(e) => setOralConfig({...oralConfig, max_duration: parseInt(e.target.value)})}
                  />
                </FormGroup>
              </FormRow>

              <FormGroup>
                <Label>视频尺寸</Label>
                <div style={{ display: 'flex', gap: '1rem' }}>
                  <label style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                    <input type="radio" checked={oralConfig.video_size === '9:16'} onChange={() => setOralConfig({...oralConfig, video_size: '9:16'})} />
                    手机竖屏 (9:16)
                  </label>
                  <label style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                    <input type="radio" checked={oralConfig.video_size === '16:9'} onChange={() => setOralConfig({...oralConfig, video_size: '16:9'})} />
                    电脑横屏 (16:9)
                  </label>
                </div>
              </FormGroup>

              <SubmitButton
                gradient="linear-gradient(135deg, #667eea, #764ba2)"
                onClick={handleSubmitOralMixCut}
                disabled={loading}
              >
                {loading ? '提交中...' : '提交口播混剪任务'}
              </SubmitButton>
            </ConfigCard>
          )}

          {/* 多镜头脚本混剪 */}
          {activeTab === 'multi-shot' && (
            <MultiShotWizard>
              <WizardSidebar>
                {[
                  { key: 'basic', label: '基础/背景音乐', required: true },
                  { key: 'shots', label: '脚本镜头素材', required: true },
                  { key: 'title', label: '视频横幅标题', required: false },
                  { key: 'subtitle', label: '口播字幕', required: false },
                  { key: 'advanced', label: '高级设置', required: false }
                ].map(step => (
                  <WizardStepBtn
                    key={step.key}
                    $active={multiShotStep === step.key}
                    onClick={() => setMultiShotStep(step.key)}
                  >
                    {step.label}{step.required && ' *'}
                  </WizardStepBtn>
                ))}
              </WizardSidebar>
              <WizardContent>
                {/* Step 1: 基础/背景音乐 */}
                {multiShotStep === 'basic' && (
                  <div>
                    <WizardStepTitle>� 基础/背景音乐</WizardStepTitle>

                    <FormGroup>
                      <Label>任务名称 *</Label>
                      <Input type="text" placeholder="请输入任务名称" value={multiShotConfig.task_name}
                        onChange={(e) => setMultiShotConfig({...multiShotConfig, task_name: e.target.value})} />
                    </FormGroup>

                    <FormRow>
                      <FormGroup>
                        <Label>剪辑数量</Label>
                        <Input type="number" min="1" max="50" value={multiShotConfig.output_count}
                          onChange={(e) => setMultiShotConfig({...multiShotConfig, output_count: parseInt(e.target.value)})} />
                      </FormGroup>
                      <FormGroup>
                        <Label>视频尺寸</Label>
                        <div style={{ display: 'flex', gap: '1rem', paddingTop: '0.5rem' }}>
                          <label style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                            <input type="radio" checked={multiShotConfig.video_size === '9:16'}
                              onChange={() => setMultiShotConfig({...multiShotConfig, video_size: '9:16'})} />
                            手机竖屏 (9:16)
                          </label>
                          <label style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                            <input type="radio" checked={multiShotConfig.video_size === '16:9'}
                              onChange={() => setMultiShotConfig({...multiShotConfig, video_size: '16:9'})} />
                            电脑横屏 (16:9)
                          </label>
                        </div>
                      </FormGroup>
                    </FormRow>

                    <WizardSubTitle>背景音乐配置</WizardSubTitle>

                    <FormGroup>
                      <Label>背景音乐音量: {multiShotConfig.bgm_volume}</Label>
                      <Input type="range" min="0" max="1" step="0.1" value={multiShotConfig.bgm_volume}
                        onChange={(e) => setMultiShotConfig({...multiShotConfig, bgm_volume: parseFloat(e.target.value)})} />
                      <SmallHint>1表示原始音量，0表示静音，0.x表示小于原始音量</SmallHint>
                    </FormGroup>

                    <FormGroup>
                      <Label>背景音乐</Label>
                      <BgmUploadArea>
                        <BgmUploadButton onClick={() => bgmFileInputRef.current?.click()} disabled={uploading}>
                          {uploading ? '上传中...' : '🎵 上传背景音乐'}
                        </BgmUploadButton>
                        <input ref={bgmFileInputRef} type="file" accept="audio/*" style={{ display: 'none' }}
                          onChange={(e) => handleFileUpload(e, 'multi_bgm')} />
                      </BgmUploadArea>
                      {multiShotConfig.bgm_urls && multiShotConfig.bgm_urls.length > 0 && (
                        <SelectedMediaList>
                          {multiShotConfig.bgm_urls.map((url, idx) => (
                            <SelectedMediaItem key={idx}>
                              <MediaIcon>🎵</MediaIcon>
                              <BgmUrl>{url}</BgmUrl>
                              <RemoveButton onClick={() => setMultiShotConfig({...multiShotConfig, bgm_urls: multiShotConfig.bgm_urls.filter((_, i) => i !== idx)})}>✕</RemoveButton>
                            </SelectedMediaItem>
                          ))}
                        </SelectedMediaList>
                      )}
                      <BgmLibrarySection>
                        <BgmLibraryTitle>📚 音频素材库</BgmLibraryTitle>
                        <MediaLibraryGrid>
                          {mediaList.filter(m => m.type === 'audio').map((media, index) => (
                            <MediaLibraryItem key={index}>
                              <MediaThumbnail>
                                <AudioPlayerInline controls>
                                  <source src={media.url} type="audio/mpeg" />
                                  <source src={media.url} type="audio/wav" />
                                  <source src={media.url} type="audio/mp3" />
                                  不支持音频播放
                                </AudioPlayerInline>
                              </MediaThumbnail>
                              <MediaInfo>
                                <MediaFileName>{media.filename}</MediaFileName>
                                <MediaSize>{formatFileSize(media.size)}</MediaSize>
                              </MediaInfo>
                              <MediaActions>
                                <AddToTaskButton onClick={() => setMultiShotConfig({...multiShotConfig, bgm_urls: [...multiShotConfig.bgm_urls, media.url]})}>+ 添加</AddToTaskButton>
                                <DeleteMediaButton onClick={() => deleteMedia(index)}>
                                  🗑️
                                </DeleteMediaButton>
                              </MediaActions>
                            </MediaLibraryItem>
                          ))}
                        </MediaLibraryGrid>
                      </BgmLibrarySection>
                    </FormGroup>

                    <WizardNav>
                      <WizardNavBtn onClick={() => setMultiShotStep('shots')} style={{ background: 'linear-gradient(135deg, #fa709a, #fee140)', color: 'white' }}>
                        下一步：脚本镜头素材 →
                      </WizardNavBtn>
                    </WizardNav>
                  </div>
                )}

                {/* Step 2: 脚本镜头素材 */}
                {multiShotStep === 'shots' && (
                  <div>
                    <WizardStepTitle>🎬 脚本镜头素材</WizardStepTitle>

                    <FormGroup>
                      <Label>脚本素材音量: {multiShotConfig.material_volume}</Label>
                      <Input type="range" min="0" max="10" step="1" value={multiShotConfig.material_volume}
                        onChange={(e) => setMultiShotConfig({...multiShotConfig, material_volume: Number(e.target.value)})} />
                      <SmallHint>0=静音，1=原始音量，10=最大音量</SmallHint>
                    </FormGroup>

                    <FormGroup>
                      <Label>镜头时间轴(秒)</Label>
                      <HintBox>⚠️ 操作提醒：每个镜头可多选素材，上传时自动归入"镜头素材"分组</HintBox>
                    </FormGroup>

                    <ShotTimelineList>
                      {multiShotConfig.shot_scripts.map((shot, shotIdx) => (
                        <ShotTimelineItem key={shotIdx}>
                          <ShotTimelineHeader>
                            <strong>镜头 {shotIdx + 1}</strong>
                            <RemoveButton onClick={() => removeShotScript(shotIdx)}>✕ 删除</RemoveButton>
                          </ShotTimelineHeader>
                          <ShotTimelineBody>
                            <div>
                              <SmallLabel>镜头时间：</SmallLabel>
                              <Input type="number" min="0.1" step="0.1" value={shot.Duration}
                                onChange={(e) => {
                                  const newShots = [...multiShotConfig.shot_scripts];
                                  newShots[shotIdx] = {...newShots[shotIdx], Duration: parseFloat(e.target.value) || 0};
                                  setMultiShotConfig({...multiShotConfig, shot_scripts: newShots});
                                }} style={{ width: '120px' }} />
                              <SmallHint>当有字幕时，镜头时间失效，镜头真实时间按字幕时长</SmallHint>
                            </div>
                            <div>
                              <SmallLabel>字幕口播内容:</SmallLabel>
                              <TextArea rows="2" placeholder="字幕口播内容,多个口播文案换行隔开" value={shot.SubtitleText || ''}
                                onChange={(e) => {
                                  const newShots = [...multiShotConfig.shot_scripts];
                                  newShots[shotIdx] = {...newShots[shotIdx], SubtitleText: e.target.value};
                                  setMultiShotConfig({...multiShotConfig, shot_scripts: newShots});
                                }} />
                            </div>
                            <div>
                              <SmallLabel>镜头素材（{shot.MediaArray?.length || 0}个）：</SmallLabel>
                              <ShotMediaInlineList>
                                {(shot.MediaArray || []).map((url, mi) => (
                                  <ShotMediaThumb key={mi}>
                                    <ShotMediaImg src={getMediaThumbnail({ url, type: 'video' }) || undefined}
                                      onError={(e) => { e.target.style.display = 'none'; }} />
                                    <ShotMediaName>{url.split('/').pop()?.split('?')[0]?.slice(-20)}</ShotMediaName>
                                    <ShotMediaRemove onClick={() => removeShotMedia(shotIdx, url)}>✕</ShotMediaRemove>
                                  </ShotMediaThumb>
                                ))}
                              </ShotMediaInlineList>
                              <ShotMediaPicker>
                                <SmallLabel>素材库快速添加：</SmallLabel>
                                <ShotMediaCheckGrid>
                                  {getFilteredMedia(t => t === 'video' || t === 'image').slice(0, 18).map((m, mi) => (
                                    <ShotMediaCheckItem key={mi} $checked={(shot.MediaArray || []).includes(m.url)}
                                      onClick={() => toggleShotMedia(shotIdx, m.url)}>
                                      <ShotMediaCheckImg src={getMediaThumbnail(m) || undefined}
                                        onError={(e) => { e.target.style.display = 'none'; }} />
                                      <ShotMediaCheckName>{m.filename?.slice(0,12)}</ShotMediaCheckName>
                                      {(shot.MediaArray || []).includes(m.url) && <ShotMediaCheckMark>✓</ShotMediaCheckMark>}
                                    </ShotMediaCheckItem>
                                  ))}
                                </ShotMediaCheckGrid>
                              </ShotMediaPicker>
                            </div>
                          </ShotTimelineBody>
                        </ShotTimelineItem>
                      ))}
                    </ShotTimelineList>

                    <AddShotSection>
                      <AddShotTitle>➕ 添加新镜头</AddShotTitle>
                      <AddShotRow>
                        <AddShotField>
                          <SmallLabel>镜头时间：</SmallLabel>
                          <Input type="number" placeholder="时间(秒)" min="0.1" step="0.1" value={newShotScript.duration}
                            onChange={(e) => setNewShotScript({...newShotScript, duration: e.target.value})} />
                          <SmallHint>当有字幕时，镜头时间失效，镜头真实时间按字幕时长</SmallHint>
                        </AddShotField>
                      </AddShotRow>
                      <AddShotRow>
                        <AddShotField>
                          <SmallLabel>字幕口播内容:</SmallLabel>
                          <TextArea placeholder="字幕口播内容,多个口播文案换行隔开" rows="2" value={newShotScript.scriptText}
                            onChange={(e) => setNewShotScript({...newShotScript, scriptText: e.target.value})} />
                        </AddShotField>
                      </AddShotRow>
                      <AddShotRow>
                        <AddShotField>
                          <SmallLabel>素材选择（{newShotScript.selectedMedia.length}个已选）：</SmallLabel>
                          <ShotMediaCheckGrid>
                            {getFilteredMedia(t => t === 'video' || t === 'image').slice(0, 18).map((m, mi) => (
                              <ShotMediaCheckItem key={mi} $checked={newShotScript.selectedMedia.includes(m.url)}
                                onClick={() => toggleNewShotMedia(m.url)}>
                                <ShotMediaCheckImg src={getMediaThumbnail(m) || undefined}
                                  onError={(e) => { e.target.style.display = 'none'; }} />
                                <ShotMediaCheckName>{m.filename?.slice(0,12)}</ShotMediaCheckName>
                                {newShotScript.selectedMedia.includes(m.url) && <ShotMediaCheckMark>✓</ShotMediaCheckMark>}
                              </ShotMediaCheckItem>
                            ))}
                          </ShotMediaCheckGrid>
                        </AddShotField>
                      </AddShotRow>
                      <AddShotRow>
                        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                          <UploadButtonSmall onClick={() => shotFileInputRef.current?.click()}>
                            📤 上传新素材
                          </UploadButtonSmall>
                          <input
                            ref={shotFileInputRef}
                            type="file"
                            accept="video/*,image/*"
                            multiple
                            style={{ display: 'none' }}
                            onChange={(e) => handleFileUpload(e, 'shot_media', '镜头素材')}
                          />
                          <SmallHint>上传后自动归入"镜头素材"分组，可在素材库中重用</SmallHint>
                        </div>
                      </AddShotRow>
                      <WizardNavBtn onClick={addShotScript} style={{ background: 'linear-gradient(135deg, var(--primary), var(--secondary))', color: 'white', marginTop: '0.75rem' }}>
                        + 添加镜头
                      </WizardNavBtn>
                    </AddShotSection>

                    <WizardNav>
                      <WizardNavBtn onClick={() => setMultiShotStep('basic')}>← 上一步</WizardNavBtn>
                      <WizardNavBtn onClick={() => setMultiShotStep('title')} style={{ background: 'linear-gradient(135deg, #fa709a, #fee140)', color: 'white' }}>
                        下一步：视频横幅标题 →
                      </WizardNavBtn>
                    </WizardNav>
                  </div>
                )}

                {/* Step 3: 视频横幅标题 */}
                {multiShotStep === 'title' && (
                  <div>
                    <WizardStepTitle>📝 视频横幅标题</WizardStepTitle>

                    <FormGroup>
                      <Label>标题</Label>
                      <TextArea rows="3" placeholder="多个随机标题可以换行" value={multiShotConfig.title}
                        onChange={(e) => setMultiShotConfig({...multiShotConfig, title: e.target.value})} />
                      <SmallHint>多个随机标题可以换行，单标题换行可以使用\n符号隔开</SmallHint>
                    </FormGroup>

                    <FormRow>
                      <FormGroup>
                        <Label>标题颜色:</Label>
                        <input type="color" value={multiShotConfig.title_color}
                          onChange={(e) => setMultiShotConfig({...multiShotConfig, title_color: e.target.value})}
                          style={{ width: '60px', height: '36px', border: 'none', cursor: 'pointer' }} />
                      </FormGroup>
                      <FormGroup>
                        <Label>标题底色:</Label>
                        <input type="color" value={multiShotConfig.title_bgcolor}
                          onChange={(e) => setMultiShotConfig({...multiShotConfig, title_bgcolor: e.target.value})}
                          style={{ width: '60px', height: '36px', border: 'none', cursor: 'pointer' }} />
                      </FormGroup>
                    </FormRow>

                    <FormGroup>
                      <Label>标题显示位置:</Label>
                      <Select value={multiShotConfig.title_position}
                        onChange={(e) => setMultiShotConfig({...multiShotConfig, title_position: e.target.value})}>
                        <option value="距离顶部10%">距离顶部10%</option>
                        <option value="距离顶部20%">距离顶部20%</option>
                        <option value="距离顶部30%">距离顶部30%</option>
                        <option value="距离顶部40%">距离顶部40%</option>
                        <option value="居中">居中</option>
                        <option value="距离顶部60%">距离顶部60%</option>
                        <option value="距离顶部70%">距离顶部70%</option>
                        <option value="距离顶部80%">距离顶部80%</option>
                        <option value="距离顶部90%">距离顶部90%</option>
                      </Select>
                    </FormGroup>

                    <FormGroup>
                      <Label>字体大小:</Label>
                      <Input type="number" min="10" max="200" value={multiShotConfig.title_font_size}
                        onChange={(e) => setMultiShotConfig({...multiShotConfig, title_font_size: parseInt(e.target.value) || 60})} />
                    </FormGroup>

                    <WizardNav>
                      <WizardNavBtn onClick={() => setMultiShotStep('shots')}>← 上一步</WizardNavBtn>
                      <WizardNavBtn onClick={() => setMultiShotStep('subtitle')} style={{ background: 'linear-gradient(135deg, #fa709a, #fee140)', color: 'white' }}>
                        下一步：口播字幕 →
                      </WizardNavBtn>
                    </WizardNav>
                  </div>
                )}

                {/* Step 4: 口播字幕 */}
                {multiShotStep === 'subtitle' && (
                  <div>
                    <WizardStepTitle>💬 口播字幕</WizardStepTitle>
                    <FormGroup>
                      <Label>全局字幕设置</Label>
                      <TextArea rows="4" placeholder="全局口播字幕内容（可选）" value={multiShotConfig.global_subtitle || ''}
                        onChange={(e) => setMultiShotConfig({...multiShotConfig, global_subtitle: e.target.value})} />
                      <SmallHint>全局字幕将应用于所有未单独设置字幕的镜头</SmallHint>
                    </FormGroup>

                    <FormRow>
                      <FormGroup>
                        <Label>字幕颜色:</Label>
                        <input type="color" value={multiShotConfig.subtitle_color || '#ffffff'}
                          onChange={(e) => setMultiShotConfig({...multiShotConfig, subtitle_color: e.target.value})}
                          style={{ width: '60px', height: '36px', border: 'none', cursor: 'pointer' }} />
                      </FormGroup>
                      <FormGroup>
                        <Label>字幕底色:</Label>
                        <input type="color" value={multiShotConfig.subtitle_bgcolor || '#000000'}
                          onChange={(e) => setMultiShotConfig({...multiShotConfig, subtitle_bgcolor: e.target.value})}
                          style={{ width: '60px', height: '36px', border: 'none', cursor: 'pointer' }} />
                      </FormGroup>
                    </FormRow>

                    <WizardNav>
                      <WizardNavBtn onClick={() => setMultiShotStep('title')}>← 上一步</WizardNavBtn>
                      <WizardNavBtn onClick={() => setMultiShotStep('advanced')} style={{ background: 'linear-gradient(135deg, #fa709a, #fee140)', color: 'white' }}>
                        下一步：高级设置 →
                      </WizardNavBtn>
                    </WizardNav>
                  </div>
                )}

                {/* Step 5: 高级设置 */}
                {multiShotStep === 'advanced' && (
                  <div>
                    <WizardStepTitle>⚙️ 高级设置</WizardStepTitle>
                    <InfoBox>
                      <h4>📌 高级设置说明</h4>
                      <p>以下为高级配置项，通常保持默认即可。</p>
                    </InfoBox>

                    <FormGroup>
                      <Label>视频编码格式</Label>
                      <Select value={multiShotConfig.video_codec || 'H.264'}
                        onChange={(e) => setMultiShotConfig({...multiShotConfig, video_codec: e.target.value})}>
                        <option value="H.264">H.264</option>
                        <option value="H.265">H.265 (HEVC)</option>
                      </Select>
                    </FormGroup>

                    <FormGroup>
                      <Label>输出帧率</Label>
                      <Select value={multiShotConfig.frame_rate || '25'}
                        onChange={(e) => setMultiShotConfig({...multiShotConfig, frame_rate: e.target.value})}>
                        <option value="24">24 fps</option>
                        <option value="25">25 fps</option>
                        <option value="30">30 fps</option>
                        <option value="60">60 fps</option>
                      </Select>
                    </FormGroup>

                    <WizardNav>
                      <WizardNavBtn onClick={() => setMultiShotStep('subtitle')}>← 上一步</WizardNavBtn>
                    </WizardNav>

                    <SubmitButton gradient="linear-gradient(135deg, #fa709a, #fee140)" onClick={handleSubmitMultiShot} disabled={loading}>
                      {loading ? '提交中...' : '提交多镜头混剪任务'}
                    </SubmitButton>
                  </div>
                )}

                {/* 多镜头提示信息 */}
                <InfoBox style={{ marginTop: '1rem' }}>
                  <h4>📌 功能说明</h4>
                  <p>按照分镜脚本精确控制每个镜头的内容和时长，适合影视集锦、产品介绍等需要精确剪辑的场景</p>
                </InfoBox>
              </WizardContent>
            </MultiShotWizard>
          )}

          {/* 快速成片 */}
          {activeTab === 'quick' && (
            <ConfigCard>
              <ConfigTitle>⚡ 快速成片</ConfigTitle>
              <InfoBox>
                <h4>📌 功能说明</h4>
                <p>
                  基于预设模板快速生成高质量视频<br/>
                  只需上传素材，系统自动完成剪辑
                </p>
              </InfoBox>

              <FormGroup>
                <Label>任务名称</Label>
                <Input
                  type="text"
                  placeholder="请输入任务名称"
                  value={quickConfig.task_name}
                  onChange={(e) => setQuickConfig({...quickConfig, task_name: e.target.value})}
                />
              </FormGroup>

              <FormGroup>
                <Label>素材上传</Label>
                <MediaGroupSelect>
                  <SmallLabel>上传分组：</SmallLabel>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                    <GroupInput 
                      placeholder="输入分组名称（可选）" 
                      value={uploadGroup}
                      onChange={(e) => setUploadGroup(e.target.value)}
                    />
                    {groupList.map(g => (
                      <GroupTag key={g.name} $active={uploadGroup === g.name} onClick={() => setUploadGroup(uploadGroup === g.name ? '' : g.name)}>
                        {g.name} ({g.count})
                      </GroupTag>
                    ))}
                  </div>
                </MediaGroupSelect>
                <UploadTypeTabs>
                  {[
                    { key: 'video', label: '📹 视频' },
                    { key: 'image', label: '🖼️ 图片' },
                    { key: 'audio', label: '🎵 音频' },
                    { key: 'all', label: '📂 全部' }
                  ].map(t => (
                    <UploadTypeTab key={t.key} $active={uploadMediaType === t.key} onClick={() => setUploadMediaType(t.key)}>
                      {t.label}
                    </UploadTypeTab>
                  ))}
                </UploadTypeTabs>
                <UploadArea>
                  <UploadButton onClick={() => fileInputRef.current?.click()} disabled={uploading}>
                    {uploading ? '上传中...' : `📤 上传${uploadMediaType === 'audio' ? '音频' : '素材'}`}
                  </UploadButton>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept={uploadMediaType === 'all' ? 'video/*,image/*,audio/*' : uploadMediaType === 'audio' ? 'audio/*' : uploadMediaType === 'image' ? 'image/*' : 'video/*'}
                    multiple
                    style={{ display: 'none' }}
                    onChange={(e) => {
                      handleFileUpload(e, uploadMediaType === 'audio' ? 'quick_bgm' : 'quick');
                    }}
                  />
                  {uploading && <UploadProgress>{uploadProgress}%</UploadProgress>}
                </UploadArea>
                
                <SelectedMediaList>
                  {quickConfig.media_urls.map((url, index) => (
                    <SelectedMediaItem key={index}>
                      <MediaIcon>📎</MediaIcon>
                      <MediaUrl>{url}</MediaUrl>
                      <RemoveButton onClick={() => removeMediaUrl(quickConfig, setQuickConfig, index)}>
                        ✕
                      </RemoveButton>
                    </SelectedMediaItem>
                  ))}
                </SelectedMediaList>

                <MediaLibrarySection>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem', flexWrap: 'wrap', gap: '0.5rem' }}>
                    <MediaLibraryTitle>📚 素材库（{getFilteredMedia(uploadMediaType === 'all' ? null : uploadMediaType === 'audio' ? t => t === 'audio' : t => t === 'video' || t === 'image').length}个）</MediaLibraryTitle>
                    <GroupFilterRow>
                      <SmallLabel>分组筛选：</SmallLabel>
                      <Select value={mediaGroupFilter} onChange={(e) => setMediaGroupFilter(e.target.value)} style={{ width: '160px' }}>
                        <option value="">全部素材</option>
                        <option value="__ungrouped__">未分组</option>
                        {groupList.map(g => (
                          <option key={g.name} value={g.name}>{g.name} ({g.count})</option>
                        ))}
                      </Select>
                    </GroupFilterRow>
                  </div>
                  {getFilteredMedia(uploadMediaType === 'all' ? null : uploadMediaType === 'audio' ? t => t === 'audio' : t => t === 'video' || t === 'image').length === 0 ? (
                    <EmptyMediaLibrary>暂无素材，请先上传</EmptyMediaLibrary>
                  ) : (
                    <MediaLibraryGrid>
                      {getFilteredMedia(uploadMediaType === 'all' ? null : uploadMediaType === 'audio' ? t => t === 'audio' : t => t === 'video' || t === 'image').map((media, index) => (
                        <MediaLibraryItem key={index}>
                          {media.group && <MediaGroupBadge>{media.group}</MediaGroupBadge>}
                          <MediaThumbnail>
                            {getMediaThumbnail(media) ? (
                              <ThumbnailImg 
                                src={getMediaThumbnail(media)} 
                                alt={media.filename}
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'flex';
                                }}
                              />
                            ) : null}
                            <ThumbnailFallback style={getMediaThumbnail(media) ? { display: 'none' } : {}}>
                              <MediaIconLarge>{getMediaIcon(media.type)}</MediaIconLarge>
                            </ThumbnailFallback>
                          </MediaThumbnail>
                          <MediaInfo>
                            <MediaFileName>{media.filename}</MediaFileName>
                            <MediaSize>{formatFileSize(media.size)}</MediaSize>
                            <GroupEditLine>
                              {editingGroup[index] ? (
                                <GroupEditForm onSubmit={(e) => { e.preventDefault(); saveGroup(index); }}>
                                  <input value={editGroupValue} onChange={(e) => setEditGroupValue(e.target.value)}
                                    style={{ width: '70px', padding: '2px 4px', fontSize: '0.7rem', background: 'var(--bg-card)', border: '1px solid var(--border-color)', color: 'var(--text-primary)', borderRadius: '3px' }}
                                    autoFocus onBlur={() => saveGroup(index)} />
                                </GroupEditForm>
                              ) : (
                                <GroupEditTag onClick={() => startEditGroup(index, media.group)} title="点击修改分组">
                                  {media.group || '未分组'} ✎
                                </GroupEditTag>
                              )}
                            </GroupEditLine>
                          </MediaInfo>
                          <MediaActions>
                            <AddToTaskButton onClick={() => {
                              if (uploadMediaType === 'audio') {
                                setQuickConfig(prev => ({
                                  ...prev,
                                  bgm_urls: [...prev.bgm_urls, media.url]
                                }));
                              } else {
                                setQuickConfig(prev => ({
                                  ...prev,
                                  media_urls: [...prev.media_urls, media.url]
                                }));
                              }
                              setMessage({ type: 'success', text: '已添加到列表' });
                            }}>
                              + 添加
                            </AddToTaskButton>
                            <DeleteMediaButton onClick={() => deleteMedia(index)}>
                              🗑️
                            </DeleteMediaButton>
                          </MediaActions>
                        </MediaLibraryItem>
                      ))}
                    </MediaLibraryGrid>
                  )}
                </MediaLibrarySection>
              </FormGroup>

              <FormGroup>
                <Label>模板ID（可选）</Label>
                <Input
                  type="text"
                  placeholder="请输入阿里云ICE模板ID"
                  value={quickConfig.template_id}
                  onChange={(e) => setQuickConfig({...quickConfig, template_id: e.target.value})}
                />
              </FormGroup>

              <FormRow>
                <FormGroup>
                  <Label>输出视频数量</Label>
                  <Input
                    type="number"
                    min="1"
                    max="50"
                    value={quickConfig.output_count}
                    onChange={(e) => setQuickConfig({...quickConfig, output_count: parseInt(e.target.value)})}
                  />
                </FormGroup>

                <FormGroup>
                  <Label>最大时长（秒）</Label>
                  <Input
                    type="number"
                    min="5"
                    max="300"
                    value={quickConfig.max_duration}
                    onChange={(e) => setQuickConfig({...quickConfig, max_duration: parseInt(e.target.value)})}
                  />
                </FormGroup>
              </FormRow>

              <SubmitButton
                gradient="linear-gradient(135deg, #43e97b, #38f9d7)"
                onClick={handleSubmitQuick}
                disabled={loading}
              >
                {loading ? '提交中...' : '提交快速成片任务'}
              </SubmitButton>
            </ConfigCard>
          )}

          {/* 剪辑结果 - 历史合成视频合集 */}
          {activeTab === 'results' && (
            <div style={{ marginTop: '20px' }}>
              <VideoCollectionHeader>
                <div>
                  <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: 'var(--primary)', fontFamily: "'Orbitron',sans-serif" }}>📹 历史合成视频合集</h2>
                  <span style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
                    共 {completedTasksByTask.length} 个任务 · {totalCompletedVideos} 个视频
                    {orphanCompletedCount > 0 && (
                      <span style={{ color: '#ffaa00', marginLeft: '0.75rem' }}>
                        ⚠️ {orphanCompletedCount} 个任务链接缺失
                      </span>
                    )}
                    {expiredTasks.length > 0 && (
                      <span style={{ color: '#ff4444', marginLeft: '0.5rem' }}>
                        · {expiredTasks.length} 条已过期
                        <CleanExpiredBtn onClick={cleanExpiredTasks}>🗑 清理</CleanExpiredBtn>
                      </span>
                    )}
                  </span>
                </div>
                <BatchActions>
                  <BatchBtn onClick={selectAllVideos}>☑ 全选</BatchBtn>
                  <BatchBtn onClick={deselectAllVideos}>☐ 取消</BatchBtn>
                  <BatchDownloadBtn onClick={batchDownloadVideos} disabled={batchDownloading || selectedVideos.size === 0}>
                    {batchDownloading ? '下载中...' : `⬇ 批量下载 (${selectedVideos.size})`}
                  </BatchDownloadBtn>
                </BatchActions>
              </VideoCollectionHeader>
              
              {completedTasksByTask.length === 0 ? (
                <EmptyState>暂无合成成功的视频</EmptyState>
              ) : (
                paginatedResults.map((task, tIdx) => (
                  <VideoTaskGroup key={tIdx}>
                    <VideoTaskGroupHeader>
                      <VideoTaskGroupTitle>
                        <SelectAllCheck onClick={() => {
                          const taskKeys = task.video_urls.map((_, vIdx) => `${tIdx}:${vIdx}`);
                          const allSelected = taskKeys.every(k => selectedVideos.has(k));
                          setSelectedVideos(prev => {
                            const next = new Set(prev);
                            taskKeys.forEach(k => allSelected ? next.delete(k) : next.add(k));
                            return next;
                          });
                        }} title="全选/取消此任务">
                          {task.video_urls.every((_, vIdx) => selectedVideos.has(`${tIdx}:${vIdx}`)) ? '☑' : '☐'}
                        </SelectAllCheck>
                        📁 {task.task_name || `任务 ${task.task_id?.slice(0,8)}`}
                        <TaskTypeBadge style={{ marginLeft: '0.5rem' }}>{getTaskTypeName(task.task_type)}</TaskTypeBadge>
                      </VideoTaskGroupTitle>
                      <VideoTaskGroupMeta>
                        <span>{task.video_count} 个视频</span>
                        <span>{formatTime(task.completed_at || task.created_at)}</span>
                      </VideoTaskGroupMeta>
                    </VideoTaskGroupHeader>
                    {task.video_count > 0 ? (
                      <VideoGrid>
                        {task.video_urls.map((url, vIdx) => {
                          const vidKey = `${tIdx}:${vIdx}`;
                          const isSelected = selectedVideos.has(vidKey);
                          return (
                          <VideoCard key={vIdx}>
                            <VideoPlayerWrapper>
                              <VideoCheckOverlay $selected={isSelected} onClick={() => toggleVideoSelect(vidKey)}>
                                <CheckCircle>{isSelected ? '✓' : ''}</CheckCircle>
                              </VideoCheckOverlay>
                              <video 
                                src={url} 
                                poster={getVideoSnapshot(url)}
                                controls
                                style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '8px' }}
                              />
                            </VideoPlayerWrapper>
                            <VideoInfo>
                              <VideoTitle>
                                {task.task_name || `视频`}
                                {task.video_count > 1 && ` (#${vIdx + 1}/${task.video_count})`}
                              </VideoTitle>
                              <VideoActions>
                                <VideoActionBtn onClick={() => downloadVideo(url, `${task.task_name || 'video'}${task.video_count > 1 ? '_' + (vIdx+1) : ''}.mp4`)}>
                                  ⬇ 下载
                                </VideoActionBtn>
                                <VideoActionBtn onClick={() => setPreviewVideo(url)} style={{ background: 'linear-gradient(135deg, var(--primary), var(--accent))' }}>
                                  ▶ 预览
                                </VideoActionBtn>
                              </VideoActions>
                            </VideoInfo>
                          </VideoCard>
                          );
                        })}
                      </VideoGrid>
                    ) : null}
                  </VideoTaskGroup>
                ))
              )}
              {totalResultsPages > 1 && (
                <ResultsPagination>
                  <ResultsPageBtn onClick={() => setResultsPage(1)} disabled={resultsPage === 1}>«</ResultsPageBtn>
                  <ResultsPageBtn onClick={() => setResultsPage(p => Math.max(1, p - 1))} disabled={resultsPage === 1}>‹</ResultsPageBtn>
                  <ResultsPageInfo>第 {resultsPage} / {totalResultsPages} 页</ResultsPageInfo>
                  <ResultsPageBtn onClick={() => setResultsPage(p => Math.min(totalResultsPages, p + 1))} disabled={resultsPage === totalResultsPages}>›</ResultsPageBtn>
                  <ResultsPageBtn onClick={() => setResultsPage(totalResultsPages)} disabled={resultsPage === totalResultsPages}>»</ResultsPageBtn>
                </ResultsPagination>
              )}
            </div>
          )}

          {/* 任务列表 */}
          <TaskSection>
            <TaskTitleRow>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <TaskTitle>📋 混剪任务列表</TaskTitle>
                <TaskCount>(共 {totalTasks} 个任务)</TaskCount>
              </div>
              <RefreshAllBtn onClick={refreshAllTasks} disabled={loading}>
                🔄 刷新全部状态
              </RefreshAllBtn>
            </TaskTitleRow>
            {tasks.length === 0 ? (
              <EmptyState>暂无混剪任务</EmptyState>
            ) : (
              <>
                <TaskList>
                  {paginatedTasks.map((task, index) => (
                    <TaskItem key={index}>
                      <TaskHeader>
                        <TaskNameRow>
                          <TaskName>{task.task_name || `任务 ${task.task_id?.slice(0,8)}`}</TaskName>
                          <TaskTypeBadge>{getTaskTypeName(task.task_type)}</TaskTypeBadge>
                          {task.status === 'completed' && (
                            <TaskVideoBadge>
                              📹 {(() => {
                                const urls = task.result_urls || [];
                                return urls.length > 0 ? urls.length : (task.result_url ? 1 : 0);
                              })()}个视频
                            </TaskVideoBadge>
                          )}
                        </TaskNameRow>
                        <TaskStatus status={task.status}>
                          {task.status === 'completed' ? '✓ 已完成' : 
                           task.status === 'processing' ? '⏳ 处理中' : 
                           task.status === 'failed' ? '✕ 失败' : 
                           task.status === 'expired' ? '⏰ 已过期' : '○ 等待中'}
                        </TaskStatus>
                      </TaskHeader>
                      <TaskBody>
                        {task.status === 'completed' ? (
                          (task.result_urls && task.result_urls.length > 0) || task.result_url ? (
                            <TaskPreview>
                              <TaskVideoRow>
                                {((task.result_urls && task.result_urls.length > 0) ? task.result_urls : [task.result_url]).slice(0, 6).map((url, vIdx) => (
                                  <TaskVideoThumb key={vIdx} onClick={() => setPreviewVideo(url)}>
                                    <img src={getVideoSnapshot(url, 1000)} alt="" 
                                      onError={(e) => { e.target.style.display = 'none'; }}
                                      style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                  </TaskVideoThumb>
                                ))}
                              </TaskVideoRow>
                              <PreviewButton onClick={() => {
                                const urls = task.result_urls && task.result_urls.length > 0 ? task.result_urls : [task.result_url];
                                setPreviewVideo(urls[0]);
                              }}>
                                ▶ 播放视频 (共{task.result_urls ? task.result_urls.length : 1}个)
                              </PreviewButton>
                            </TaskPreview>
                          ) : (
                            <TaskPreview>
                              <CompletedWithoutVideo>
                                <span>✓</span>
                                <span>合成完成</span>
                                <span style={{fontSize: '0.8rem', color: '#666', marginTop: '5px'}}>视频链接暂不可用（ICE记录已过期）</span>
                              </CompletedWithoutVideo>
                            </TaskPreview>
                          )
                        ) : task.status === 'processing' ? (
                        <TaskPreview>
                          <ProcessingPlaceholder>
                            <ProcessingSpinner />
                            <span>合成中...</span>
                          </ProcessingPlaceholder>
                        </TaskPreview>
                      ) : task.status === 'failed' ? (
                        <TaskPreview>
                          <FailedPlaceholder>
                            <span>❌</span>
                            <span>合成失败</span>
                          </FailedPlaceholder>
                        </TaskPreview>
                      ) : (
                        <TaskPreview>
                          <PendingPlaceholder>
                            <span>⏳</span>
                            <span>等待处理</span>
                          </PendingPlaceholder>
                        </TaskPreview>
                      )}
                      <TaskDetails>
                        <TaskMeta>
                          <TaskMetaItem>
                            <MetaLabel>任务ID</MetaLabel>
                            <MetaValue>{task.task_id?.slice(0,12)}...</MetaValue>
                          </TaskMetaItem>
                          <TaskMetaItem>
                            <MetaLabel>进度</MetaLabel>
                            <MetaValue>
                              <ProgressBar>
                                <ProgressFill width={task.progress || 0} status={task.status} />
                              </ProgressBar>
                              <ProgressText>{task.progress || 0}%</ProgressText>
                            </MetaValue>
                          </TaskMetaItem>
                          <TaskMetaItem>
                            <MetaLabel>创建时间</MetaLabel>
                            <MetaValue>{formatTime(task.created_at)}</MetaValue>
                          </TaskMetaItem>
                          {task.completed_at && (
                            <TaskMetaItem>
                              <MetaLabel>完成时间</MetaLabel>
                              <MetaValue>{formatTime(task.completed_at)}</MetaValue>
                            </TaskMetaItem>
                          )}
                          {task.render_duration != null && (
                            <TaskMetaItem>
                              <MetaLabel>渲染耗时</MetaLabel>
                              <MetaValue>{formatDuration(task.render_duration)}</MetaValue>
                            </TaskMetaItem>
                          )}
                        </TaskMeta>
                        {task.error_message && (
                          <TaskError>错误: {task.error_message}</TaskError>
                        )}
                        <TaskActions>
                          {task.result_url && task.status === 'completed' && (
                            <>
                              <TaskActionBtn primary onClick={() => setPreviewVideo(task.result_url)}>
                                ▶ 预览
                              </TaskActionBtn>
                              <TaskActionBtn onClick={() => downloadVideo(task.result_url, `${task.task_name || 'mixcut'}.mp4`)}>
                                ⬇ 下载
                              </TaskActionBtn>
                            </>
                          )}
                          {task.status === 'processing' && (
                            <TaskActionBtn onClick={() => checkTaskStatus(task.task_id)}>
                              🔄 刷新状态
                            </TaskActionBtn>
                          )}
                          <TaskActionBtn onClick={() => handleEditTask(task)}>
                            ✏️ 编辑
                          </TaskActionBtn>
                          <TaskActionBtn onClick={() => handleCopyTask(task.task_id)}>
                            📋 复制
                          </TaskActionBtn>
                          <TaskActionBtn 
                            danger 
                            onClick={() => deleteTask(task.task_id)}
                            style={{ background: '#dc3545', color: 'white' }}
                          >
                            🗑 删除
                          </TaskActionBtn>
                        </TaskActions>
                      </TaskDetails>
                    </TaskBody>
                  </TaskItem>
                ))}
              </TaskList>
              
              <RenderPagination />
              </>
            )}
          </TaskSection>

          {/* 任务编辑弹窗 */}
          {editModalOpen && (
            <PreviewOverlayBg onClick={() => setEditModalOpen(false)}>
              <PreviewModal onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
                <PreviewHeader>
                  <PreviewTitle>✏️ 编辑任务</PreviewTitle>
                  <PreviewCloseBtn onClick={() => setEditModalOpen(false)}>✕</PreviewCloseBtn>
                </PreviewHeader>
                <div style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <FormGroup>
                    <Label>任务名称</Label>
                    <Input value={editTaskName} onChange={(e) => setEditTaskName(e.target.value)} placeholder="任务名称" />
                  </FormGroup>
                  <FormGroup>
                    <Label>剪辑数量</Label>
                    <Input type="number" min="1" max="50" value={editOutputCount} onChange={(e) => setEditOutputCount(parseInt(e.target.value))} />
                  </FormGroup>
                  <FormGroup>
                    <Label>最大时长(秒)</Label>
                    <Input type="number" min="5" max="300" value={editMaxDuration} onChange={(e) => setEditMaxDuration(parseInt(e.target.value))} />
                  </FormGroup>
                  <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                    <ActionButton onClick={() => setEditModalOpen(false)}>取消</ActionButton>
                    <ActionButton gradient="linear-gradient(135deg, var(--primary), var(--secondary))" onClick={saveEditTask}>保存</ActionButton>
                  </div>
                </div>
              </PreviewModal>
            </PreviewOverlayBg>
          )}

          {/* 视频预览弹窗 */}
          {previewVideo && (
            <PreviewOverlayBg onClick={() => setPreviewVideo(null)}>
              <PreviewModal onClick={(e) => e.stopPropagation()}>
                <PreviewHeader>
                  <PreviewTitle>视频预览</PreviewTitle>
                  <PreviewCloseBtn onClick={() => setPreviewVideo(null)}>✕</PreviewCloseBtn>
                </PreviewHeader>
                <PreviewContent>
                  <video 
                    controls 
                    autoPlay 
                    style={{ width: '100%', maxHeight: '70vh', borderRadius: '8px' }}
                    src={previewVideo}
                  >
                    您的浏览器不支持视频播放
                  </video>
                </PreviewContent>
                <PreviewFooter>
                  <PreviewDownloadBtn 
                    href={previewVideo} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    onClick={(e) => {
                      e.preventDefault();
                      downloadVideo(previewVideo, 'mixcut_video.mp4');
                    }}
                  >
                    ⬇ 下载视频
                  </PreviewDownloadBtn>
                  <PreviewOpenBtn 
                    href={previewVideo} 
                    target="_blank" 
                    rel="noopener noreferrer"
                  >
                    🔗 在新窗口打开
                  </PreviewOpenBtn>
                </PreviewFooter>
              </PreviewModal>
            </PreviewOverlayBg>
          )}
        </div>
      </MainContent>
    </PageContainer>
  );
};

// 样式组件
const PageContainer = styled.div`
  min-height: 100vh;
  background: transparent;
`;

const Header = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 0 1.5rem 0;
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const LogoIcon = styled.span`
  font-size: 1.5rem;
`;

const LogoText = styled.h1`
  font-family: 'Orbitron', sans-serif;
  font-size: 1.5rem;
  font-weight: bold;
  color: var(--primary);
`;

const UserMenu = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
`;

const ActionButton = styled.button`
  padding: 0.5rem 1rem;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  cursor: pointer;
  font-family: 'Orbitron', sans-serif;
  font-size: 0.85rem;
  transition: all 0.3s;
  background: transparent;
  color: var(--text-secondary);
  
  &:hover {
    border-color: var(--primary);
    color: var(--primary);
    box-shadow: var(--glow-primary);
  }
`;

const MainContent = styled.main`
  padding: 0;
`;

const TabContainer = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-bottom: 2rem;
  flex-wrap: wrap;
`;

const TabButton = styled(ActionButton)`
  flex: 1;
  min-width: 200px;
  padding: 0.9rem;
  font-size: 0.9rem;
  background: ${props => props.$active ? 'var(--bg-card)' : 'transparent'};
  color: ${props => props.$active ? 'var(--primary)' : 'var(--text-secondary)'};
  border-color: ${props => props.$active ? 'var(--primary)' : 'var(--border-color)'};
  &:hover {
    border-color: var(--primary);
    color: var(--primary);
  }
`;

const MessageBox = styled.div`
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1.5rem;
  background: ${props => props.type === 'success' ? 'rgba(0,255,136,0.1)' : 'rgba(255,51,102,0.1)'};
  color: ${props => props.type === 'success' ? '#00ff88' : '#ff3366'};
  border: 1px solid ${props => props.type === 'success' ? 'rgba(0,255,136,0.3)' : 'rgba(255,51,102,0.3)'};
`;

const ConfigCard = styled.div`
  background: var(--bg-card);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  padding: 2rem;
  margin-bottom: 2rem;
`;

const ConfigTitle = styled.h2`
  font-family: 'Orbitron', sans-serif;
  font-size: 1.3rem;
  margin-bottom: 1.5rem;
  color: var(--primary);
`;

const InfoBox = styled.div`
  background: rgba(0, 240, 255, 0.05);
  border-left: 4px solid var(--primary);
  padding: 1rem;
  margin-bottom: 1.5rem;
  border-radius: 4px;
  
  h4 {
    margin: 0 0 0.5rem 0;
    color: var(--primary);
  }
  
  p {
    margin: 0;
    color: var(--text-secondary);
    line-height: 1.6;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: var(--text-primary);
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  font-size: 1rem;
  background: rgba(18, 18, 26, 0.8);
  color: var(--text-primary);
  
  &:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: var(--glow-primary);
  }
  &::placeholder {
    color: var(--text-secondary);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  font-size: 1rem;
  background: rgba(18, 18, 26, 0.8);
  color: var(--text-primary);
  &:focus {
    outline: none;
    border-color: var(--primary);
  }
  option {
    background: var(--bg-dark);
    color: var(--text-primary);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  font-size: 1rem;
  resize: vertical;
  background: rgba(18, 18, 26, 0.8);
  color: var(--text-primary);
  
  &:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: var(--glow-primary);
  }
  &::placeholder {
    color: var(--text-secondary);
  }
`;

const MediaUrl = styled.span`
  flex: 1;
  font-size: 0.9rem;
  color: var(--text-secondary);
  word-break: break-all;
`;

const RemoveButton = styled.button`
  background: var(--danger);
  color: white;
  border: none;
  border-radius: 4px;
  padding: 0.25rem 0.5rem;
  cursor: pointer;
  font-size: 0.8rem;
  
  &:hover {
    opacity: 0.8;
  }
`;

const AddButton = styled.button`
  background: linear-gradient(135deg, var(--primary), var(--secondary));
  color: white;
  border: none;
  border-radius: 6px;
  padding: 0.75rem 1.5rem;
  cursor: pointer;
  font-size: 1rem;
  white-space: nowrap;
  
  &:hover {
    box-shadow: var(--glow-primary);
  }
`;

const AddShotForm = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 0.5rem;
  flex-wrap: wrap;
  
  input:first-child {
    width: 120px;
  }
`;

const AddShotSection = styled.div`
  margin-top: 1rem;
  padding: 1rem;
  background: rgba(18, 18, 26, 0.5);
  border: 1px solid var(--border-color);
  border-radius: 8px;
`;

const AddShotTitle = styled.div`
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--primary);
  margin-bottom: 0.75rem;
`;

const AddShotRow = styled.div`
  margin-bottom: 0.75rem;
`;

const AddShotField = styled.div`
  display: flex;
  flex-direction: column;
`;

const UploadButtonSmall = styled.button`
  padding: 0.5rem 0.75rem;
  background: linear-gradient(135deg, var(--primary), var(--secondary));
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.85rem;
  white-space: nowrap;
  &:hover { box-shadow: var(--glow-primary); }
`;

const ShotList = styled.div`
  margin-bottom: 1rem;
`;

const ShotItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background: rgba(18, 18, 26, 0.6);
  border: 1px solid var(--border-color);
  border-radius: 6px;
  margin-bottom: 0.5rem;
`;

const ShotInfo = styled.div`
  flex: 1;
  font-size: 0.9rem;
  color: var(--text-primary);
  line-height: 1.6;
  
  small {
    color: var(--text-secondary);
    font-size: 0.8rem;
  }
`;

const SubmitButton = styled(ActionButton)`
  width: 100%;
  padding: 1rem;
  font-size: 1.1rem;
  margin-top: 1rem;
  background: ${props => props.gradient || 'linear-gradient(135deg, var(--primary), var(--secondary))'};
  color: white;
  border: none;
  &:hover {
    box-shadow: var(--glow-primary);
  }
`;

const TaskSection = styled.div`
  background: var(--bg-card);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  padding: 2rem;
`;

const TaskTitle = styled.h2`
  font-family: 'Orbitron', sans-serif;
  font-size: 1.3rem;
  margin-bottom: 0;
  color: var(--primary);
`;

const TaskCount = styled.span`
  font-size: 0.85rem;
  font-weight: normal;
  color: var(--text-secondary);
  margin-left: 0.5rem;
`;

const TaskTitleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const RefreshAllBtn = styled.button`
  padding: 0.5rem 1rem;
  border: 1px solid var(--primary);
  border-radius: 6px;
  background: transparent;
  color: var(--primary);
  cursor: pointer;
  font-size: 0.85rem;
  transition: all 0.3s;
  &:hover:not(:disabled) {
    background: rgba(0, 240, 255, 0.1);
    box-shadow: var(--glow-primary);
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CleanExpiredBtn = styled.button`
  padding: 2px 8px;
  margin-left: 6px;
  border: 1px solid #ff4444;
  border-radius: 4px;
  background: transparent;
  color: #ff4444;
  cursor: pointer;
  font-size: 0.7rem;
  transition: all 0.2s;
  &:hover { background: rgba(255,68,68,0.15); }
`;

const BatchActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
`;

const BatchBtn = styled.button`
  padding: 0.35rem 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  background: var(--bg-card);
  color: var(--text-secondary);
  cursor: pointer;
  font-size: 0.8rem;
  transition: all 0.2s;
  &:hover { border-color: var(--primary); color: var(--primary); }
`;

const BatchDownloadBtn = styled.button`
  padding: 0.4rem 1rem;
  border: none;
  border-radius: 6px;
  background: linear-gradient(135deg, var(--primary), var(--secondary));
  color: white;
  cursor: pointer;
  font-size: 0.85rem;
  font-weight: 500;
  transition: all 0.3s;
  &:hover:not(:disabled) { box-shadow: var(--glow-primary); }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const SelectAllCheck = styled.span`
  cursor: pointer;
  font-size: 1.1rem;
  line-height: 1;
  color: var(--primary);
  margin-right: 0.25rem;
  user-select: none;
  &:hover { opacity: 0.7; }
`;

const VideoCheckOverlay = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 10;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 2px solid ${props => props.$selected ? 'var(--primary)' : 'rgba(255,255,255,0.5)'};
  background: ${props => props.$selected ? 'var(--primary)' : 'rgba(0,0,0,0.4)'};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  &:hover { border-color: var(--primary); }
`;

const CheckCircle = styled.span`
  color: #000;
  font-size: 0.9rem;
  font-weight: bold;
  line-height: 1;
`;

const TaskList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const TaskItem = styled.div`
  padding: 1rem;
  background: rgba(18, 18, 26, 0.6);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  border-left: 4px solid var(--primary);
  transition: all 0.3s;
  
  &:hover {
    border-color: var(--primary);
    box-shadow: var(--glow-primary);
  }
`;

const TaskHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
`;

const TaskNameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const TaskName = styled.span`
  font-weight: 600;
  color: var(--text-primary);
  font-size: 1rem;
`;

const TaskStatus = styled.span`
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.85rem;
  font-weight: 500;
  background: ${props => 
    props.status === 'completed' ? 'rgba(0,255,136,0.15)' :
    props.status === 'processing' ? 'rgba(255,170,0,0.15)' :
    props.status === 'failed' ? 'rgba(255,51,102,0.15)' : 'rgba(160,160,176,0.15)'};
  color: ${props => 
    props.status === 'completed' ? '#00ff88' :
    props.status === 'processing' ? '#ffaa00' :
    props.status === 'failed' ? '#ff3366' : '#a0a0b0'};
  border: 1px solid ${props =>
    props.status === 'completed' ? 'rgba(0,255,136,0.3)' :
    props.status === 'processing' ? 'rgba(255,170,0,0.3)' :
    props.status === 'failed' ? 'rgba(255,51,102,0.3)' : 'rgba(160,160,176,0.3)'};
`;

const TaskTypeBadge = styled.span`
  padding: 0.15rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  background: rgba(0, 240, 255, 0.15);
  color: var(--primary);
  font-weight: 500;
  border: 1px solid var(--border-color);
`;

const ProgressBar = styled.div`
  width: 80px;
  height: 6px;
  background: rgba(255,255,255,0.1);
  border-radius: 3px;
  overflow: hidden;
  display: inline-block;
  vertical-align: middle;
  margin-right: 6px;
`;

const ProgressFill = styled.div`
  height: 100%;
  width: ${props => props.width || 0}%;
  border-radius: 3px;
  transition: width 0.5s ease;
  background: ${props => 
    props.status === 'completed' ? 'linear-gradient(90deg, var(--primary), var(--success))' :
    props.status === 'processing' ? 'linear-gradient(90deg, var(--warning), var(--primary))' :
    props.status === 'failed' ? 'var(--danger)' : '#555'};
`;

const ProgressText = styled.span`
  font-size: 0.8rem;
  color: var(--text-secondary);
  vertical-align: middle;
`;

const TaskBody = styled.div`
  display: flex;
  gap: 1rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const TaskPreview = styled.div`
  position: relative;
  width: 160px;
  min-height: 100px;
  flex-shrink: 0;
  border-radius: 6px;
  overflow: hidden;
  cursor: pointer;
  
  @media (max-width: 768px) {
    width: 100%;
    max-width: 240px;
  }
`;

const VideoThumbnail = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 6px;
  background: rgba(0,0,0,0.5);
  display: block;
`;

const PreviewOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1rem;
  font-weight: 600;
  opacity: 0;
  transition: opacity 0.3s;
  border-radius: 6px;
  
  ${TaskPreview}:hover & {
    opacity: 1;
  }
`;

const SnapshotGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  padding: 10px;
  background: rgba(0,0,0,0.3);
  border-radius: 8px;
`;

const SnapshotItem = styled.div`
  position: relative;
  aspect-ratio: 4/3;
  border-radius: 4px;
  overflow: hidden;
  cursor: pointer;
  background: rgba(255,255,255,0.05);
`;

const SnapshotOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.2rem;
  opacity: 0;
  transition: opacity 0.2s;
  
  &:hover {
    opacity: 1;
  }
`;

const PreviewButton = styled.button`
  display: block;
  width: 100%;
  padding: 10px;
  margin-top: 10px;
  background: linear-gradient(135deg, var(--primary), var(--secondary));
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: var(--glow-primary);
  }
`;

const CompletedWithoutVideo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 30px;
  background: rgba(0, 255, 136, 0.1);
  border: 1px solid rgba(0, 255, 136, 0.2);
  border-radius: 8px;
  color: #00ff88;
  
  span:first-child {
    font-size: 2.5rem;
    margin-bottom: 10px;
  }
  
  span:nth-child(2) {
    font-size: 1.1rem;
    font-weight: 600;
  }
`;

const ProcessingPlaceholder = styled.div`
  width: 100%;
  aspect-ratio: 16/9;
  background: rgba(255, 170, 0, 0.1);
  border: 1px solid rgba(255, 170, 0, 0.2);
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  color: #ffaa00;
  font-size: 0.85rem;
`;

const ProcessingSpinner = styled.div`
  width: 24px;
  height: 24px;
  border: 2px solid rgba(255, 170, 0, 0.2);
  border-top-color: #ffaa00;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const FailedPlaceholder = styled.div`
  width: 100%;
  aspect-ratio: 16/9;
  background: rgba(255, 51, 102, 0.1);
  border: 1px solid rgba(255, 51, 102, 0.2);
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  color: #ff3366;
  font-size: 0.85rem;
`;

const PendingPlaceholder = styled.div`
  width: 100%;
  aspect-ratio: 16/9;
  background: rgba(160, 160, 176, 0.1);
  border: 1px solid rgba(160, 160, 176, 0.2);
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  color: var(--text-secondary);
  font-size: 0.85rem;
`;

const TaskDetails = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const TaskMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
`;

const TaskMetaItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
`;

const MetaLabel = styled.span`
  font-size: 0.7rem;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const MetaValue = styled.span`
  font-size: 0.85rem;
  color: var(--text-primary);
  font-weight: 500;
`;

const TaskError = styled.div`
  font-size: 0.8rem;
  color: var(--danger);
  background: rgba(255, 51, 102, 0.1);
  border: 1px solid rgba(255, 51, 102, 0.2);
  padding: 0.4rem 0.75rem;
  border-radius: 4px;
  word-break: break-all;
`;

const TaskActions = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 0.25rem;
`;

const TaskActionBtn = styled.button`
  padding: 0.4rem 0.85rem;
  border: 1px solid var(--border-color);
  border-radius: 5px;
  cursor: pointer;
  font-size: 0.8rem;
  font-weight: 500;
  transition: all 0.2s;
  background: ${props => props.primary ? 'linear-gradient(135deg, var(--primary), var(--secondary))' : 'transparent'};
  color: ${props => props.primary ? 'white' : 'var(--text-secondary)'};
  
  &:hover {
    border-color: var(--primary);
    color: ${props => props.primary ? 'white' : 'var(--primary)'};
    transform: translateY(-1px);
    box-shadow: var(--glow-primary);
  }
`;

const Pagination = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  margin-top: 1.5rem;
  padding: 1rem;
  background: rgba(18, 18, 26, 0.6);
  border: 1px solid var(--border-color);
  border-radius: 8px;
`;

const PaginationBtn = styled.button`
  padding: 0.5rem 1rem;
  border: 1px solid var(--border-color);
  border-radius: 5px;
  background: transparent;
  color: var(--text-secondary);
  cursor: pointer;
  font-size: 0.85rem;
  transition: all 0.2s;
  
  &:hover:not(:disabled) {
    border-color: var(--primary);
    color: var(--primary);
    box-shadow: var(--glow-primary);
  }
  
  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
`;

const PageInfo = styled.span`
  font-size: 0.85rem;
  color: var(--text-secondary);
`;

// 视频预览弹窗样式
const PreviewOverlayBg = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 2rem;
`;

const PreviewModal = styled.div`
  background: var(--bg-card);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  max-width: 800px;
  width: 100%;
  overflow: hidden;
`;

const PreviewHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid var(--border-color);
`;

const PreviewTitle = styled.h3`
  margin: 0;
  font-size: 1.1rem;
  color: var(--primary);
`;

const PreviewCloseBtn = styled.button`
  background: none;
  border: none;
  font-size: 1.2rem;
  cursor: pointer;
  color: var(--text-secondary);
  padding: 0.25rem;
  line-height: 1;
  
  &:hover {
    color: var(--primary);
  }
`;

const PreviewContent = styled.div`
  padding: 1rem;
  background: #000;
`;

const PreviewFooter = styled.div`
  display: flex;
  gap: 0.75rem;
  padding: 1rem 1.5rem;
  border-top: 1px solid var(--border-color);
`;

const PreviewDownloadBtn = styled.button`
  flex: 1;
  padding: 0.6rem 1rem;
  background: linear-gradient(135deg, var(--primary), var(--secondary));
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 500;
  text-align: center;
  
  &:hover {
    box-shadow: var(--glow-primary);
  }
`;

const PreviewOpenBtn = styled.a`
  flex: 1;
  padding: 0.6rem 1rem;
  background: transparent;
  color: var(--text-secondary);
  border: 1px solid var(--border-color);
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 500;
  text-align: center;
  text-decoration: none;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    border-color: var(--primary);
    color: var(--primary);
  }
`;

// 历史视频合集样式
const VideoCollectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const VideoTaskGroup = styled.div`
  background: var(--bg-card);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
`;

const VideoTaskGroupHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid var(--border-color);
`;

const VideoTaskGroupTitle = styled.div`
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-primary);
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const VideoTaskGroupMeta = styled.div`
  display: flex;
  gap: 1rem;
  font-size: 0.8rem;
  color: var(--text-secondary);
`;

const EmptySubState = styled.div`
  text-align: center;
  padding: 2rem;
  color: var(--text-secondary);
  font-size: 0.9rem;
  opacity: 0.7;
`;

const ResultsPagination = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.5rem;
  margin-top: 1.5rem;
  padding: 0.75rem;
`;

const ResultsPageBtn = styled.button`
  padding: 0.35rem 0.7rem;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  background: var(--bg-card);
  color: var(--text-secondary);
  cursor: pointer;
  font-size: 0.85rem;
  transition: all 0.2s;
  &:hover:not(:disabled) { border-color: var(--primary); color: var(--primary); }
  &:disabled { opacity: 0.3; cursor: not-allowed; }
`;

const ResultsPageInfo = styled.span`
  font-size: 0.85rem;
  color: var(--text-secondary);
  padding: 0 0.5rem;
`;

const TaskVideoBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 0.7rem;
  background: rgba(0,255,136,0.1);
  color: #00ff88;
  border: 1px solid rgba(0,255,136,0.2);
`;

const TaskVideoRow = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
`;

const TaskVideoThumb = styled.div`
  width: 48px;
  height: 85px;
  border-radius: 4px;
  overflow: hidden;
  cursor: pointer;
  border: 1px solid var(--border-color);
  background: rgba(0,0,0,0.5);
  transition: border-color 0.2s;
  aspect-ratio: 9/16;
  flex-shrink: 0;
  &:hover { border-color: var(--primary); }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem;
  color: var(--text-secondary);
  font-size: 1.1rem;
`;

const UploadArea = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
`;

const UploadButton = styled.button`
  background: linear-gradient(135deg, var(--primary), var(--secondary));
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.75rem 1.5rem;
  cursor: pointer;
  font-size: 1rem;
  font-weight: 500;
  transition: all 0.3s;
  
  &:hover:not(:disabled) {
    box-shadow: var(--glow-primary);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const UploadTypeTabs = styled.div`
  display: flex;
  gap: 0.4rem;
  margin-bottom: 0.75rem;
  flex-wrap: wrap;
`;

const UploadTypeTab = styled.button`
  padding: 0.35rem 0.75rem;
  border-radius: 6px;
  border: 1px solid ${props => props.$active ? 'var(--primary)' : 'var(--border-color)'};
  background: ${props => props.$active ? 'rgba(0,240,255,0.15)' : 'transparent'};
  color: ${props => props.$active ? 'var(--primary)' : 'var(--text-secondary)'};
  font-size: 0.8rem;
  cursor: pointer;
  transition: all 0.2s;
  &:hover { border-color: var(--primary); color: var(--primary); }
`;

const UploadProgress = styled.span`
  color: var(--primary);
  font-weight: 600;
`;

const SelectedMediaList = styled.div`
  margin-bottom: 1rem;
  max-height: 200px;
  overflow-y: auto;
`;

const SelectedMediaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  background: rgba(18, 18, 26, 0.6);
  border: 1px solid var(--border-color);
  border-radius: 6px;
  margin-bottom: 0.5rem;
`;

const MediaIcon = styled.span`
  font-size: 1.2rem;
`;

const MediaLibrarySection = styled.div`
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid var(--border-color);
`;

const MediaLibraryTitle = styled.h3`
  font-size: 1rem;
  margin-bottom: 1rem;
  color: var(--text-primary);
`;

const EmptyMediaLibrary = styled.div`
  text-align: center;
  padding: 2rem;
  color: var(--text-secondary);
  background: rgba(18, 18, 26, 0.4);
  border: 1px solid var(--border-color);
  border-radius: 8px;
`;

const MediaLibraryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 1rem;
  max-height: 400px;
  overflow-y: auto;
`;

const MediaLibraryItem = styled.div`
  background: var(--bg-card);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  overflow: hidden;
  transition: all 0.3s;
  
  &:hover {
    border-color: var(--primary);
    box-shadow: var(--glow-primary);
  }
`;

const MediaThumbnail = styled.div`
  height: 120px;
  background: rgba(0,0,0,0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  position: relative;
`;

const ThumbnailImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const AudioPlayerInline = styled.audio`
  width: 100%;
  height: 40px;
  border-radius: 4px;
  background: rgba(0,0,0,0.5);
  &::-webkit-media-controls-panel {
    background: rgba(18,18,26,0.9);
  }
  &::-webkit-media-controls-current-time-display,
  &::-webkit-media-controls-time-remaining-display {
    color: var(--text-primary);
    font-size: 10px;
  }
`;

const ThumbnailFallback = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const MediaIconLarge = styled.span`
  font-size: 3rem;
`;

const MediaInfo = styled.div`
  padding: 0.75rem;
`;

const MediaFileName = styled.div`
  font-size: 0.85rem;
  color: var(--text-primary);
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const MediaSize = styled.div`
  font-size: 0.75rem;
  color: var(--text-secondary);
  margin-top: 0.25rem;
`;

const GroupEditLine = styled.div`
  margin-top: 0.25rem;
`;

const GroupEditTag = styled.span`
  display: inline-block;
  padding: 1px 6px;
  border-radius: 8px;
  font-size: 0.65rem;
  cursor: pointer;
  background: rgba(0,240,255,0.1);
  color: var(--primary);
  border: 1px solid var(--border-color);
  &:hover { background: rgba(0,240,255,0.2); }
`;

const GroupEditForm = styled.form`
  display: inline-block;
`;

const MediaActions = styled.div`
  display: flex;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  border-top: 1px solid var(--border-color);
`;

const AddToTaskButton = styled.button`
  flex: 1;
  background: linear-gradient(135deg, var(--primary), var(--secondary));
  color: white;
  border: none;
  border-radius: 4px;
  padding: 0.5rem;
  cursor: pointer;
  font-size: 0.85rem;
  
  &:hover {
    box-shadow: var(--glow-primary);
  }
`;

const DeleteMediaButton = styled.button`
  background: var(--danger);
  color: white;
  border: none;
  border-radius: 4px;
  padding: 0.5rem 0.75rem;
  cursor: pointer;
  font-size: 0.85rem;
  
  &:hover {
    opacity: 0.8;
  }
`;

const BgmUploadArea = styled.div`
  margin-bottom: 1rem;
`;

const BgmUploadButton = styled.button`
  background: linear-gradient(135deg, var(--primary), var(--secondary));
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.75rem 1.5rem;
  cursor: pointer;
  font-size: 1rem;
  font-weight: 500;
  transition: all 0.3s;
  
  &:hover:not(:disabled) {
    box-shadow: var(--glow-primary);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const BgmSelected = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem;
  background: rgba(18, 18, 26, 0.6);
  border: 1px solid var(--border-color);
  border-radius: 6px;
  margin-bottom: 1rem;
`;

const BgmUrl = styled.span`
  flex: 1;
  font-size: 0.85rem;
  color: var(--text-secondary);
  word-break: break-all;
`;

// 多镜头向导样式
const MultiShotWizard = styled.div`
  display: flex;
  gap: 1.5rem;
  background: var(--bg-card);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  margin-bottom: 2rem;
  min-height: 500px;
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const WizardSidebar = styled.div`
  width: 200px;
  min-width: 180px;
  background: rgba(18, 18, 26, 0.6);
  border-right: 1px solid var(--border-color);
  border-radius: 12px 0 0 12px;
  padding: 1rem 0;
  @media (max-width: 768px) {
    width: 100%;
    border-right: none;
    border-bottom: 1px solid var(--border-color);
    border-radius: 12px 12px 0 0;
    display: flex;
    overflow-x: auto;
    padding: 0.5rem;
  }
`;

const WizardStepBtn = styled.button`
  display: block;
  width: 100%;
  padding: 0.75rem 1rem;
  text-align: left;
  border: none;
  background: ${props => props.$active ? 'rgba(0,240,255,0.1)' : 'transparent'};
  color: ${props => props.$active ? 'var(--primary)' : 'var(--text-secondary)'};
  font-size: 0.85rem;
  cursor: pointer;
  border-left: 3px solid ${props => props.$active ? 'var(--primary)' : 'transparent'};
  transition: all 0.2s;
  &:hover {
    background: rgba(0,240,255,0.05);
    color: var(--primary);
  }
  @media (max-width: 768px) {
    width: auto;
    white-space: nowrap;
    border-left: none;
    border-bottom: 2px solid ${props => props.$active ? 'var(--primary)' : 'transparent'};
  }
`;

const WizardContent = styled.div`
  flex: 1;
  padding: 2rem;
`;

const WizardStepTitle = styled.h3`
  font-family: 'Orbitron', sans-serif;
  font-size: 1.2rem;
  margin-bottom: 1.5rem;
  color: var(--primary);
  padding-bottom: 0.5rem;
  border-bottom: 2px solid var(--primary);
  display: inline-block;
`;

const WizardSubTitle = styled.h4`
  font-size: 1rem;
  margin: 1.5rem 0 1rem 0;
  color: var(--text-primary);
`;

const WizardNav = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 1.5rem;
  padding-top: 1rem;
  border-top: 1px solid var(--border-color);
`;

const WizardNavBtn = styled.button`
  padding: 0.6rem 1.2rem;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  background: transparent;
  color: var(--text-secondary);
  cursor: pointer;
  font-size: 0.9rem;
  transition: all 0.2s;
  &:hover {
    border-color: var(--primary);
    color: var(--primary);
  }
`;

const ShotTimelineList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin: 1rem 0;
`;

const ShotTimelineItem = styled.div`
  border: 1px solid var(--border-color);
  border-radius: 8px;
  padding: 1rem;
  background: rgba(18, 18, 26, 0.4);
`;

const ShotTimelineHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px dashed var(--border-color);
  color: var(--text-primary);
`;

const ShotTimelineBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const SmallLabel = styled.span`
  font-size: 0.8rem;
  color: var(--text-secondary);
  display: block;
  margin-bottom: 0.25rem;
`;

const SmallHint = styled.p`
  font-size: 0.75rem;
  color: #666;
  margin: 0.25rem 0 0 0;
`;

// 镜头素材多选网格样式
const ShotMediaInlineList = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  margin-bottom: 0.5rem;
`;

const ShotMediaThumb = styled.div`
  position: relative;
  width: 64px;
  height: 48px;
  border-radius: 4px;
  overflow: hidden;
  border: 1px solid var(--border-color);
  background: rgba(0,0,0,0.5);
`;

const ShotMediaImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const ShotMediaName = styled.span`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  font-size: 0.5rem;
  color: #fff;
  background: rgba(0,0,0,0.7);
  padding: 1px 3px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ShotMediaRemove = styled.button`
  position: absolute;
  top: 0;
  right: 0;
  width: 14px;
  height: 14px;
  border: none;
  background: var(--danger);
  color: white;
  font-size: 0.55rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0 4px 0 4px;
  padding: 0;
  line-height: 1;
  &:hover { opacity: 0.8; }
`;

const ShotMediaPicker = styled.div`
  margin-top: 0.5rem;
  padding: 0.5rem;
  background: rgba(18, 18, 26, 0.3);
  border: 1px solid var(--border-color);
  border-radius: 6px;
`;

const ShotMediaCheckGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 4px;
  max-height: 200px;
  overflow-y: auto;
  @media (max-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const ShotMediaCheckItem = styled.div`
  position: relative;
  aspect-ratio: 4/3;
  border-radius: 4px;
  overflow: hidden;
  cursor: pointer;
  border: 2px solid ${props => props.$checked ? 'var(--primary)' : 'transparent'};
  background: rgba(0,0,0,0.5);
  transition: border-color 0.2s;
  &:hover { border-color: var(--primary); }
`;

const ShotMediaCheckImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: ${props => props.alt ? '1' : '0.5'};
`;

const ShotMediaCheckName = styled.span`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  font-size: 0.5rem;
  color: #fff;
  background: rgba(0,0,0,0.7);
  padding: 1px 2px;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ShotMediaCheckMark = styled.span`
  position: absolute;
  top: 2px;
  right: 2px;
  width: 16px;
  height: 16px;
  background: var(--primary);
  color: #000;
  border-radius: 50%;
  font-size: 0.6rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
`;

const MediaGroupSelect = styled.div`
  margin-bottom: 0.75rem;
  padding: 0.75rem;
  background: rgba(18, 18, 26, 0.4);
  border: 1px solid var(--border-color);
  border-radius: 8px;
`;

const GroupInput = styled.input`
  padding: 0.4rem 0.6rem;
  border: 1px solid var(--border-color);
  border-radius: 4px;
  background: rgba(18, 18, 26, 0.8);
  color: var(--text-primary);
  font-size: 0.8rem;
  width: 180px;
  &:focus { outline: none; border-color: var(--primary); }
  &::placeholder { color: var(--text-secondary); }
`;

const GroupTag = styled.span`
  display: inline-block;
  padding: 0.25rem 0.6rem;
  border-radius: 12px;
  font-size: 0.75rem;
  cursor: pointer;
  background: ${props => props.$active ? 'rgba(0,240,255,0.2)' : 'rgba(255,255,255,0.05)'};
  color: ${props => props.$active ? 'var(--primary)' : 'var(--text-secondary)'};
  border: 1px solid ${props => props.$active ? 'var(--primary)' : 'var(--border-color)'};
  transition: all 0.2s;
  &:hover { border-color: var(--primary); color: var(--primary); }
`;

const GroupFilterRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const MediaGroupBadge = styled.span`
  position: absolute;
  top: 4px;
  right: 4px;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 0.65rem;
  background: rgba(0,240,255,0.2);
  color: var(--primary);
  z-index: 2;
  pointer-events: none;
`;

const HintBox = styled.div`
  background: rgba(255, 170, 0, 0.1);
  border: 1px solid rgba(255, 170, 0, 0.3);
  color: #ffaa00;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  font-size: 0.85rem;
  margin: 0.5rem 0;
`;

const BgmLibrarySection = styled.div`
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid var(--border-color);
`;

const BgmLibraryTitle = styled.h3`
  font-size: 1rem;
  margin-bottom: 0.75rem;
  color: var(--text-primary);
`;

// 剪辑结果页面样式
const VideoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 1.25rem;
`;

const VideoCard = styled.div`
  background: var(--bg-card);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  overflow: hidden;
  transition: transform 0.2s, box-shadow 0.2s, border-color 0.2s;
  max-width: 320px;
  margin: 0 auto;
  
  &:hover {
    transform: translateY(-2px);
    border-color: var(--primary);
    box-shadow: var(--glow-primary);
  }
`;

const VideoPlayerWrapper = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 9 / 16;
  background: #000;
  max-height: 480px;
  overflow: hidden;
  
  video {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const VideoInfo = styled.div`
  padding: 12px;
`;

const VideoTitle = styled.div`
  font-size: 0.9rem;
  color: var(--text-primary);
  margin-bottom: 10px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const VideoActions = styled.div`
  display: flex;
  gap: 8px;
`;

const VideoActionBtn = styled.button`
  flex: 1;
  padding: 8px 12px;
  background: linear-gradient(135deg, var(--primary), var(--secondary));
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 0.85rem;
  cursor: pointer;
  transition: opacity 0.2s;
  
  &:hover {
    box-shadow: var(--glow-primary);
  }
`;

export default MixCutPage;
